/* tslint:disable:import-spacing no-inferrable-types variable-name align no-trailing-whitespace */
// noinspection JSUnusedGlobalSymbols

import { DateTime } from 'luxon';

import { isarray, isset } from '../../util/util';
import { Status } from '../../util/status';
import { StatusReporter } from '../../util/status.reporter';
import { Logger } from '../../util/logger';
import { Train, TrainType } from './train';
import { Track, TrackType } from './track';
import { DecisionPoint, DecisionPointKind } from './decision.point';
import { TrainClass, TrainDirection } from './train.class';
import { Connection } from './connection';
import { WorkBlock, WorkType } from './work.block';
import { Diversion, DiversionType } from './diversion';
import { TSO } from './t.s.o';
import { TSOLink } from './t.s.o.link';
import { TSOSpeed } from './t.s.o.speed';
import { Penalty } from './penalty';
import { StopConsist } from './stop.consist';
import { StopEvent } from './stop.event';
import { Stop } from './stop';
import { ActualTrain } from './actual.train';
import { EvenTrain } from './even.train';
import { ExponentialTrain } from './exponential.train';
import { ScheduledTrain } from './scheduled.train';
import { WeeklyTrain } from './weekly.train';
import { Period } from '../../util/period';
import { Movement } from './movement';
import { MovementHistory } from './movement.history';
import { DelayType, MovementDelay } from './movement.delay';
import { DistanceLocator } from './distance.locator';
import { InterLink } from './inter.link';
import { QuadTree } from '../../util/quad.tree';
import { ConsoleStatusReporter } from '../../util/console.status.reporter';

export interface TrainStopEntry {
     dpId: number;
     name: string;
  mileage: number;
     type: string;
   bundle: number;
     time: string;
    hours: number;
     stop: Stop|null;
}

export interface TrainStopReport {
       train: Train;
       stops: TrainStopEntry[];
   totalTime: string;
  totalHours: number;
}

/**
 *
 * @class Scenario
 *
 * @brief the combined simulation input definition and output data.  This is the core data object
 * used for viewing in the string line viewer.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      August 2019
 *
 */
export class Scenario extends Status implements DistanceLocator {

  /** the raw plant definition (given to the simulator */
  private           _plant: any;

  /** the raw simulation output data */
  private       _movements: any;

  /** the diamond crossings if we have them */
  private _crossings: any;

  /* - - - - - - meta data - - - - - - - - - -  */

  public              _tag: string = '';
  public               _id: number = 0;
  public           _author: string = '';
  public         _inputVer: string = '';
  public         _timezone: string = 'UTC';

  /** @var Map<number, TrainClass> the lookup table for train classes, indexed by id */
  private    _trainClasses: Map<number, TrainClass> = new Map<number, TrainClass>();

  /** @var number the train class ID we use when the train class is missing */
  private missingTrainClass: number = 2;

  /** @var Map<number, DecisionPoint> the lookup table for decision points, indexed by id */
  private             _dps: Map<number, DecisionPoint> = new Map<number, DecisionPoint>();

  /** @var Map<number, [number, number][]> the lookup table for crossing data of a decision point */
  private             _dpCrossings: Map<number, {segments: [number, number][]}> = new Map<number, {segments: [number, number][]}>();

  /** @var Map<number, Set<DecisionPoint>> map of link/Track id to the before/after decision points */
  private        _dpOfLink: Map<number, Set<DecisionPoint>> = new Map<number, Set<DecisionPoint>>();

  /** @var DecisionPoint[] dps ordered by distance with mike 0 (east side) at end of array */
  private        _distance: DecisionPoint[] = [];

  /** @var Map<number, StopConsist> the set of train parameter changes (sometimes used by Stop objects) */
  private     _stopConsist: Map<number, StopConsist> = new Map<number, StopConsist>();

  /** @var Map<number, Stop> the set of train stops, brings together StopConsist, StopEvent and *Train. */
  private           _stops: Map<number, Stop> = new Map<number, Stop>();

  /** @var Map<number, WorkBlock|null> the set of active work zones */
  private      _workBlocks: Map<number, WorkBlock|null> = new Map<number, WorkBlock>();
  private _workBlocksReservedIds: number[] = [];

  /** @var Map<number, Diversion|null> are the set of one way train (group) diversions */
  private      _diversions: Map<number, Diversion|null> = new Map<number, Diversion>();
  private _diversionReservedIds: number[] = [];

  /** @var Map<number, TSO|null> are the set of one slow orders */
  private      _tso: Map<number, TSO|null> = new Map<number, TSO>();
  private _tsoReservedIds: number[] = [];

  // noinspection JSMismatchedCollectionQueryUpdate
  /** @var Set<number> all possible time samples. */
  private      _timePoints: Set<number>     = new Set<number>();

  /** @var Map<number, Track> the lookup table for tracks, indexed by id */
  private           _links: Map<number, Track> = new Map<number, Track>();

  /** @var Map<number, Track[]> links grouped into their mileage bundles */
  private     _linkMileage: Map<number, Track[]> = new Map<number, Track[]>();

  /** @var InterLink[] the top level bundles of tracks, from west to east */
  private      _interlinks: InterLink[] = [];

  /** @var _actTrains the ActualTrain templates indexed by their id. */
  private       _actTrains: Map<number, ActualTrain> = new Map<number, ActualTrain>();

  /** @var _evnTrains the EvenTrain objects templates by their id. */
  private       _evnTrains: Map<number, EvenTrain> = new Map<number, EvenTrain>();

  /** @var _expTrains the ExponentialTrain templates indexed by their id. */
  private       _expTrains: Map<number, ExponentialTrain> = new Map<number, ExponentialTrain>();

  /** @var _schTrains the ScheduledTrain templates indexed by their id. */
  private       _schTrains: Map<number, ScheduledTrain> = new Map<number, ScheduledTrain>();

  /** @var _wkyTrains the ScheduledTrain templates indexed by their id. */
  private       _wkyTrains: Map<number, WeeklyTrain> = new Map<number, WeeklyTrain>();

  /** @var _simTrains the instances of trains in the simulation, indexed by unique (generated) id */
  private       _simTrains: Map<number, Train> = new Map<number, Train>();

  /** @var _iidTrains the instances of trains in the simulation, indexed by the iid() string */
  private       _iidTrains: Map<string, Train> = new Map<string, Train>();

  private           _moves: Map<number, Movement> = new Map<number, Movement>();

  /** @var _tMin the minimum time point for all trains */
  public             _tMin: number     = 99999999;

  /** @var _tMax the minimum time point for all trains */
  public             _tMax: number     = 0;

  /** @var _dMin the minimum distance location for all trains */
  public             _dMin: number     = 99999999;

  /** @var _dMax the maximum distance location for all trains */
  public             _dMax: number     = 0;

  /* _startTrack first west side track */
  public       _startTrack: Track|null = null;

  /* _endTrack the first track (east side) */
  public         _endTrack: Track|null = null;

  /** @var _statusRep the StatusReporter to use for publishing updates */
  private readonly _statusRep: StatusReporter|null = null;

  /** @var _quad the bounding box search tree we use to look up movements by co-ords */
  private _quad: QuadTree|null         = null;

  /** @var _job the details around the simulation job */
  private _job: any                    = null;

  /** @var _userLog if we have user logging, this is the collection name */
  private _userLog: string             = '';

  /** @var _userLogURL if we have user logging this is the full URL to the MongoDB storage */
  private _userLogURL: string          = '';

  /** @var _options simulation options used for this particular simulation */
  private _options: any                = null;

  /** @brief helper to fetch all the possible train movement lines */
  public trainMovements(): MovementHistory[] {

    const data: MovementHistory[] = [];

    for (const v of this._simTrains.values()) {

      if (v._history) {

        data.push(v._history);
      }
    }

    return data;
  }

  /** helper to find the tracks that a westbound train would include in the low/high bracket */
  public westBoundBetween(low: number, high: number): Track[] {

    /* make sure mileage is in right order */

    if (low > high) {

      const tmp = low;
      low       = high;
      high      = tmp;
    }

    /* interlinks for scenario are stored with easternmost (0 mp) at the end (right side) */

    let      il: InterLink[] = this._interlinks.slice().reverse();
    let   start: number      = this._interlinks.length;

    /* the interlinks (bundles) now have easternmost at i=0, move up to the minimum interlink */

    {
      for (let i = 0; i < il.length; i++) {

        if (il[i]._order > low) {

          break;
        }

        /* we want the lowest distance that a
        westbound train can touch in this range */

        start = i;
      }
    }

    if (start >= il.length) {

      /* not a valid range */

      return [];
    }

    /* now walk forward, harvesting tracks as we go until we exceed the high mileage */

    const seen: Map<number, Track> = new Map<number, Track>();

    for (let i = start; i < il.length; i++) {


      if (!il[i]._before || !il[i]._after) {

        continue;
      }

      // @ts-ignore
      if ((il[i]._before._mileage > high) && (il[i]._after._mileage > high)) {

        /* we're done, more westward would be completely outside our range */

        break;
      }

      /* now walk all the tracks (links) in this bundle and keep the ones we want */

      il[i]._tracks.forEach((track: Track) => {

        /* ignore junctions */

        if (track._type == TrackType.JUNCTION) {

          return ;
        }

        /* if the track end points are both completely outside our range, toss it */


        if ((track._before && track._before._mileage < low) &&
            (track._after && track._after._mileage < low)) {

          /* outside our range */

          return ;
        }

        /* add to our bag (a hash map) */

        if (seen.has(track._id)) {

          return ;
        }

        seen.set(track._id, track);

      });

    } /* end of decision point walk */

    /* pass 'em back, convert the iterable collection to a normal array */

    return Array.from(seen.values());
  }

  /** helper to find the first decision point that is before or at this mileage. */
  public lowestDecisionPoint(mileage: number): DecisionPoint|null {

    const mp: DecisionPoint[] = this.milePoints();

    if (mp.length === 0) {

      return null;
    }

    let match: DecisionPoint|null = null;

    for (let i=0; i<mp.length; i++) {

      if (mp[i]._mileage > mileage) {

        break;
      }

      match = mp[i];
    }

    return match;
  }

  /** @brief fetch all decision points, sorted so that mile 0 is first. */
  public milePoints(reverse: boolean = false): DecisionPoint[] {

    const posts: DecisionPoint[] = Array.from(this._dps.values());

    if (reverse) {

      posts.sort((a, b): number => {

        if (a._mileage > b._mileage) {
          return -1;
        }

        if (a._mileage < b._mileage) {
          return 1;
        }

        return 0;
      });

    } else {

      posts.sort((a, b): number => {

        if (a._mileage < b._mileage) {
          return -1;
        }

        if (a._mileage > b._mileage) {
          return 1;
        }

        return 0;
      });
    }

    return posts;
  }

  /** @brief fetch all possible time points */
  public hourPoints() {

    const hours: number[] = Array.from(this._timePoints.values());

    hours.sort((a, b): number => {

      if (a < b) {
        return -1;
      }

      if (a > b) {
        return 1;
      }

      return 0;
    });

    return hours;
  }

  /** fetch the train class that gets used for missing train classes */
  public getMissingTrainClass(): TrainClass|null {

    return this._trainClasses.get(this.missingTrainClass) ?? null;
  }

  /** @brief fetch all the train (speed) classes, sorted by name */
  public trainClasses(): TrainClass[] {

    const classes: TrainClass[] = Array.from(this._trainClasses.values());

    classes.sort((a, b): number => {

      if (a._name < b._name) {
        return -1;
      }

      if (a._name > b._name) {
        return 1;
      }

      return 0;
    });

    return classes;
  }

  /** @brief helper to look up a single movement by its datapoint id */
  movement(id: number): Movement|null {

    if (!this._moves.has(id)) {

      return null;
    }

    return this._moves.get(id) ?? null;
  }

  /** @brief fetch the train (and their movement history. Indexed by generated id */
  trains(): Map<number, Train> {

    return this._simTrains;
  }

  /** @brief fetch the work blocks */
  workBlocks(): Map<number, WorkBlock|null> {

    return this._workBlocks;
  }

  /** fetch the crossing data indexed by decision point */
  crossings(): Map<DecisionPoint, {segments: [number, number][]}> {

    let data: Map<DecisionPoint, {segments: [number, number][]}> = new Map<DecisionPoint, {segments: [number, number][]}>();

    for (let [did, item] of this._dpCrossings) {

      let dp: DecisionPoint|null = this._dps.get(did) ?? null;

      if (!dp) {

        continue;
      }

      data.set(dp, item);
    }

    /* pass it back */

    return data;
  }

  /** @brief fetch the diversions */
  diversions(): Map<number, Diversion|null> {

    return this._diversions;
  }

  /** @brief fetch the diversions */
  tso(): Map<number, TSO|null> {

    return this._tso;
  }

  /** fetch / reserve a diversion that hasn't been used yet. */
  allocateTSOId(): number {

    /* find the current max id for valid TSO */

    let max = 0;

    for (const b of this._tso.values()) {

      if (!b) {

        continue;
      }

      if (b._id > max) {

        max = b._id;
      }

    }

    /*
     * we can't reuse any id we've previously allocated, because we support "undo"
     * in the GUI...so a block with an already allocated ID may actually come
     * around again.  Once we allocate...we can't reuse 'em
     *
     */

    for (const r of this._tsoReservedIds) {

      if (r > max) {

        max = r;
      }
    }

    /* go one past anything we have or have used before */

    max++;

    /* reserve it */

    this._tso.set(max, null);
    this._tsoReservedIds.push(max);

    /* pass back the allocated id */

    return max;
  }

  /** fetch / reserve a diversion that hasn't been used yet. */
  allocateDiversionId(): number {

    /* find the current max id for valid diversions */

    let max = 0;

    for (const b of this._diversions.values()) {

      if (!b) {

        continue;
      }

      if (b._id > max) {

        max = b._id;
      }

    }

    /*
     * we can't reuse any id we've previously allocated, because we support "undo"
     * in the GUI...so a block with an already allocated ID may actually come
     * around again.  Once we allocate...we can't reuse 'em
     *
     */

    for (const r of this._diversionReservedIds) {

      if (r > max) {

        max = r;
      }
    }

    /* go one past anything we have or have used before */

    max++;

    /* reserve it */

    this._diversions.set(max, null);
    this._diversionReservedIds.push(max);

    /* pass back the allocated id */

    return max;
  }

  /** fetch / reserve a work block that hasn't been used yet. */
  allocateWorkBlockId(): number {

    /* find the current max id for valid blocks */

    let max = 0;

    for (const b of this._workBlocks.values()) {

      if (!b) {

        continue ;
      }

      if (b._id > max) {

        max = b._id;
      }

    }

    /*
     * we can't reuse any id we've previously allocated, because we support "undo"
     * in the GUI...so a block with an already allocated ID may actually come
     * around again.  Once we allocate...we can't reuse 'em
     *
     */

    for (const r of this._workBlocksReservedIds) {

      if (r > max) {

        max = r;
      }
    }

    /* go one past anything we have or have used before */

    max++;

    /* reserve it */

    this._workBlocks.set(max, null);
    this._workBlocksReservedIds.push(max);

    /* pass back the allocated id */

    return max;
  }

  /** @brief optionally trace the scenario construction */
  status(text: string): void {

    if ((this._statusRep === undefined) || (this._statusRep === null)) {
      return ;
    }

    this._statusRep.status(text);
  }

  /** @brief look up nearby trains for this (word space) location */
  near(x: number, y: number): Movement[] {

    if (!this._quad) {

      return [];
    }

    const matches: Movement[] = this._quad.find(x, y) as Movement[];

    if ((matches == null) || (matches.length === 0)) {

      /* not even remotely close */

      return [];
    }

    return matches;
  }

  /** @brief find train movements that intersect with this line */
  intersects(px: number, py: number, qx: number, qy: number): Movement[] {

    if (!this._quad) {

      return [];
    }

    const hits: Movement[] = this._quad.intersects(px, py, qx, qy) as Movement[];

    if ((hits == null) || (hits.length === 0)) {

      return [];
    }

    return hits;
  }

  /** @brief given a distance we return an interlink that contains that distance */
  interLinkOfMile(distance: number): InterLink|null {

    let  start: number    = 0;
    let    end: number    = this._interlinks.length - 1;

    /* binary search */

    while (start <= end) {

      const mid: number     = Math.floor((start + end) / 2);

      /* because we are comparing a range look at the one beside us as well */

      if ((mid !== end) && (this._interlinks[end].contains(distance) != null)) {

        return this._interlinks[end];
      }

      const item: InterLink = this._interlinks[mid];

      if (item.contains(distance) != null) {

        return item;
      }

      /* look in left or right half accordingly */

      if (item._order > distance) {

        start = mid + 1;

      } else {

        end = mid - 1;
      }

    }

    return null;
  }

  /**
   *
   * @brief standard constructor
   *
   * @param plant     object             the simulation definition as given via API or remote user.
   * @param movements object|null       (optional if null) the output data from the line simulator.
   * @param statusRep StatusReporter the hook for getting progress updates
   * @param tz        string         the timezone
   */
  constructor(plant: any, movements: object|null=null, statusRep: StatusReporter|null=null, tz: string='UTC') {

    super();

    this.unReady();

    this._timezone     = tz;

    this._plant        = null;
    this._job          = null;
    this._movements    = null;
    this._crossings    = null;

    this.status('loading scenario...');

    if (!statusRep) {

      statusRep = new ConsoleStatusReporter();
    }

    this._statusRep    = statusRep;

    if (!isset(plant.scenario)) {

      this.setError('Can not find top level scenario.');

      return ;
    }

    if (movements) {

      if (!isset((movements as any).sim_results)) {

        this.setError('Can not find top level train movements.');

        return;
      }
    }

    this._plant     = plant.scenario;

    if (movements != null) {

      if (typeof (movements as any).sim_results != 'undefined') {

        this._movements = (movements as any).sim_results;

        if (typeof this._movements.diamond_xings != 'undefined') {

          this._crossings = this._movements.diamond_xings;
        }
      }
    }

    /*
     * fetch the job details, if we have it.  It lets us know things like timing and if user logging
     * was enabled.
     *
     */

    this._options = {
      author:       "ironroad",
      client_id:    0,
      command:      "job",
      dispatch:     "workblox",
      file:         "",
      jsonexport:   "",
      lockup_hours: 720,
      loglevel:     "error",
      logpath:      "",
      max_days:     30,
      passthrough:  true,
      permissive:   true,
      priority:     1,
      subcommand:   "",
      trace_end:    "",
      trace_start:  "",
      tracefile:    "",
      userlog:      "",
    };

    if ((movements != null) && (typeof (movements as any).job_detail != 'undefined') && ((movements as any).job_detail)) {

      this._job = (movements as any).job_detail;

      /* if we have the job details...we might have user logging; i.e. where it's stored in MongoDB ... */

      if (typeof (movements as any).job_detail.userlog_location != "undefined") {

        this._userLog    = (movements as any).job_detail.userlog_location.collection;
        this._userLogURL = (movements as any).job_detail.userlog_location.url;
      }

      /* if we have the options, pull out the simulation options as well. */

      if (typeof (movements as any).job_detail.options != "undefined") {

        this._options  = JSON.parse((movements as any).job_detail.options);

      }
    }

    /* fetch the metadata */

    if (!this.scanMeta()) {

      /* could not read metadata */

      return ;
    }

    this.status('scenario: tag: ' + this._tag + ' id: ' + this._id + ' author: ' + this._author);

    /* read in the key data we need to work with the output data symbolically */

    /* train classes */

    if (!this.scanTrainClasses()) {

      return ;
    }

    /* decision points */

    if (!this.scanDecisionPoints()) {

      return ;
    }

    /* now that we have decision points, we can load in the crossing segments for each one. */

    if (!this.scanDiamondCrossings()) {

      return ;
    }

    /* links */

    if (!this.scanLinks()) {

      return ;
    }

    /* run times */

    if (!this.scanRuntimes()) {

      return ;
    }

    /* connection time penalties */

    if (!this.scanConnectionPenalties()) {

      return ;
    }

    /* the actual connections (routes) */

    if (!this.scanConnections()) {

      return ;
    }

    /* actual trains */

    if (!this.scanActualTrains()) {

      return ;
    }

    /* even trains */

    if (!this.scanEvenTrains()) {

      return ;
    }

    /* exponential trains */

    if (!this.scanExponentialTrains()) {

      return ;
    }

    /* scheduled trains */

    if (!this.scanScheduledTrains()) {

      return ;
    }

    /* weekly trains */

    if (!this.scanWeeklyTrains()) {

      return ;
    }

    /* train parameter changes (for some stops) */

    if (!this.scanStopConsist()) {

      return ;
    }

    /* train stops */

    if (!this.scanStops()) {

      return ;
    }

    /* the activities at train stops */

    if (!this.scanStopEvents()) {

      return ;
    }

    /* the work block zones */

    if (!this.scanWorkBlocks()) {

      return ;
    }

    /* the one way diversions (if we have them) */

    if (!this.scanDiversions()) {

      return ;
    }

    /* the TSO (if we have them) */

    if (!this.scanTSO()) {

      return ;
    }

    if (!this.scanTSOLinks()) {

      return ;
    }

    if (!this.scanTSOSpeeds()) {

      return ;
    }

    /* make the generated trains from the templates we scanned above ... */

    if (this._movements != null) {

      if (!this.generateTrains()) {

        return;
      }

      /* add the movement history to all trains */

      if (!this.buildMovementHistory()) {

        return;
      }
    }

    /*
     * the decision point look up table ordered by distance, with east being 0 miles and west
     * being maximum miles.  East is placed on the right side (end of array).
     *
     */

    this.status('building decision point distance lookup ... ');

    for (const value of this._dps.values()) {

      this._distance.push(value);
    }

    this._distance.sort((a, b): number => {

      if (a._mileage > b._mileage) {
        return -1;
      }

      if (a._mileage < b._mileage) {
        return 1;
      }

      return 0;
    });

    {
      let j = 0;

      for (const dp of this._distance) {

        dp._order = j;

        if (dp._order === 0) {

          dp._type = DecisionPointKind.BEGIN;

        } else if (dp._order === (this._distance.length - 1)) {

          dp._type = DecisionPointKind.END;

        } else {

          dp._type = DecisionPointKind.INTERNAL;
        }

        j++;
      }
    }

    /* build the link mileage mapping...this allows us to build the interlink map (overall summary graph) */

    this.status('building link mileage index ... ');

    for (const [lid, l] of this._links) {

      /*
       * figure out the before and after decision point.  The bigger mileage one is on the left,
       * because railway convention is that mile marker 0 is on the East Coast.
       *
       */

      let setValues : Set<DecisionPoint>|undefined = this._dpOfLink.get(lid);

      if (!setValues) {

        continue;
      }

      const linkedDps: DecisionPoint[]    = Array.from(setValues.values());
      let         dp1: DecisionPoint|null = null;
      let         dp2: DecisionPoint|null = null;
      let     mileage: number             = -1;

      /* get the pair of decision points and their kind, start/end and junction links will only have a single decision point. */

      dp1 = linkedDps[0];
      const kind1: DecisionPointKind = dp1._type;
      let   kind2: DecisionPointKind = DecisionPointKind.NULL;

      if (linkedDps.length > 1) {

        dp2   = linkedDps[1];
        kind2 = dp2._type;
      }

      /*
       * if there is only one decision point, and it's a terminal link hen we can figure out start/end links based on the kind
       * of decision point.
       *
       */

      if ((linkedDps.length === 1) && (l._type === TrackType.TERMINAL)) {

        if (kind1 === DecisionPointKind.BEGIN) {

          this._startTrack = l;
        }

        if (kind1 === DecisionPointKind.END) {

          this._endTrack = l;
        }

        if (kind2 === DecisionPointKind.BEGIN) {

          this._startTrack = l;
        }

        if (kind2 === DecisionPointKind.END) {

          this._endTrack = l;
        }

      }

      /* set the mile marker for this link, mainly just used for ordering the links */

      if ((kind1 !== DecisionPointKind.NULL) && (kind2 !== DecisionPointKind.NULL)) {

        /* ensure left - right ordering */

        if (dp2) {

          if (dp1._mileage < dp2._mileage) {

            const tmp: DecisionPoint = dp1;

            dp1 = dp2;
            dp2 = tmp;
          }
        }
      }

      /* make sure the first one is the non-null decision point */

      if (kind1 === DecisionPointKind.NULL) {

        const tmp: DecisionPoint = dp1;
        dp1 = dp2;
        dp2 = tmp;

      }

      if (dp1) {

        mileage = dp1._mileage;
      }

      /* save the ordering of decision points */

      l._before = dp1;
      l._after  = dp2;

      /* double check at the ends of the network ... */

      if (dp2 == null) {

        /* west side terminal */

        if (l._type === TrackType.TERMINAL) {

          if (dp1 && (dp1._order === (this._distance.length - 1))) {

            /* east side terminal */

            mileage = 0.0;

          } else {

            /* west side terminal */

            mileage = 100000.0;
          }

        }
      }

      if (dp1 == null) {

        /* east side terminal */

        if (l._type === TrackType.TERMINAL) {

          mileage = 0.0;
        }
      }

      /* save/group by the mile marker*/

      if (!this._linkMileage.has(mileage)) {

        this._linkMileage.set(mileage, []);
      }

      const ary: Track[]|undefined = this._linkMileage.get(mileage);

      if (ary) {

        ary.push(l);

        this._linkMileage.set(mileage, ary);
      }

    } /* end of link walk*/

    /* finally, since we now have links bundled by mile marker, we can build the interlinks (summary graph) */

    this.status('building interlink map...');

    /*
     * walk the mileage map; it already groups the links by similar mileage, i.e. all the links
     * between the same two decision points, will be linked together.
     *
     */

    for (const [mileage, tracks] of this._linkMileage) {

      if (tracks.length === 0) {

        continue;
      }

      /* make sure the tracks are sorted by order */

      tracks.sort((a, b): number => {

        if (a._track < b._track) {
          return 1;
        }

        return 0;
      });

      if (tracks.length === 0) {

        /* empty interlink? */

        Logger.error('[scenario] interlink is empty, aborting (', tracks[0]._before, ' MP).', tracks);

        return ;
      }

      /* find the first track that isn't a junction */

      let firstTrack: Track|null = null;

      for (const trk of tracks) {

        if (trk._type === TrackType.JUNCTION) {

          continue;
        }

        firstTrack = trk;
      }

      if (!firstTrack) {

        /* there was no non-junction track! */

        Logger.error('[scenario] interlink has only Junction tracks.  not usable, aborting (', tracks[0]._before, ' MP).', tracks);

        return ;
      }

      /* create a new interlink... */

      const bundle: InterLink = new InterLink(mileage, tracks, firstTrack._before, firstTrack._after);

      /* link up the LinkProperty to its interlink ... */

      for (const track of tracks) {

        track._peers = bundle;
      }

      /* set the interlink index */

      this._interlinks.push(bundle);
    }

    /* make sure the interlinks are sorted by mileage */

    this._interlinks.sort((a: InterLink, b: InterLink): number => {

      if (a._order > b._order) {
        return -1;
      }

      if (a._order < b._order) {
        return 1;
      }

      return 0;
    });

    for (let i = 0; i < this._interlinks.length; i++) {

      this._interlinks[i]._index = i;
    }

    this._interlinks[0]._westMost                           = true;
    this._interlinks[this._interlinks.length - 1]._eastMost = true;

    /* good to go */

    this.makeReady();

    this.status('scenario ready.');

    /* no more need for the input file data, we have everything internal now. (save some memory) */

    /*
     * we now have to keep these around, because they may be needed by the timezone() method in order
     * to clone new instances that are "cast" into a target timezone.
     *
     *
    this._plant     = null;
    this._movements = null;
     *
     */

    /*
     * finally scan the delays for trains; we have to do it at the end, because some of the processing
     * requires knowing adjacent decision points or connecting tracks.
     *
     */

    if (!this.scanDelays()) {

      return;
    }

    if (!this.setDefaultConsist()) {

      return;
    }
  }

  /** return this UTC seconds converted to seconds in the current timezone */
  tzSeconds(seconds: number): number {

    /* it's a "null" value, just leave it as is. */

    if (seconds == 0) {

      return 0;
    }

    /* we presume simulation data "as is" is already UTC */

    if (this._timezone == 'UTC') {

      /* no conversion, the "base" is presumed to be UTC */

      return seconds;
    }

    /*
     * we're "casting" int a different timezone, but the GUI still works with seconds,
     * in "unix epoch" format, so we can't just stamp it with a timezone...it's just a
     * number of seconds.  But, what we can do is get Luxon to tell us what the offset
     * is for the target timezone, and then just adjust the seconds by that amount.
     *
     * This way the entire GUI continues to do what it does...but now the times are
     * relative to the desired timezone.
     *
     */

    let off: string   = DateTime.fromSeconds(seconds, {zone: this._timezone}).toFormat("ZZ");
    let hm: string[]  = off.split(/[^0-9]+/).filter(x => x);
    let dir: number   = -1;
    let h: number     = Number.parseInt(hm[0]);
    let m: number     = Number.parseInt(hm[1]);

    if (/^\+/.test(off)) {

      dir = 1;
    }

    /* adjust */

    let secOffset = dir * ((3600 * h) + (60 * m));

    /* return the adjusted time */

    return seconds + secOffset;
  }

  /** create a copy of this model, that is in the given timezone */
  timezone(tz: string = 'UTC'): Scenario {

    this.status(`spawning timezone: ${tz}`);

    /* call the constructor as it was previously called...but now with new timezone */

    return new Scenario(
      {scenario: this._plant},
      {sim_results: this._movements, job_detail: this._job},
      this._statusRep,
      tz);

  }

  /** @brief fetch the start of the user logging window (in simulation time seconds) */
  traceStartSeconds() {

    if ((typeof this._options.trace_start == 'undefined') || (this._options.trace_start == '')) {

      return 0;
    }

    return Period.toSeconds(this._options.trace_start);
  }

  /** @brief fetch the end of the user logging window (in simulation time seconds) */
  traceEndSeconds() {

    if ((typeof this._options.trace_end == 'undefined') || (this._options.trace_end == '')) {

      return 0;
    }

    return Period.toSeconds(this._options.trace_end);
  }

  /** @brief pre-load user-logging related extra data */
  preloadUserLogging(data: any): void {


    if (typeof data.workFlags != 'undefined') {

      /* load in the work block related flags for trains at decision points */

      data.workFlags.forEach((item: any) => {

        let train: Train|undefined = this.trainByIID(item.pa);

        if (!train) {

          return ;
        }

        train.setWorkBlockFlags(item.dp.i, item.cw, item.hw);

      });

    }


  }

  /** fetch the work block related status flags for this train at this decision point */
  workFlags(dp: DecisionPoint, train: Train): [boolean, boolean] {

    let iid: string   = Train.iid(train);
    let target: Train|undefined = this._iidTrains.get(iid);

    if (!target) {

      return [false, false];
    }

    return train.getWorkBlockFlags(dp._id);
  }

  /** @brief fetch a train by its iid() string */
  trainByIID(iid: string) {

    return this._iidTrains.get(iid);
  }

  /** @brief test if this scenario was done with debugging (user logging) */
  hasUserLogging(): boolean {

    return this._userLog != '';
  }

  /** @brief fetch the name/tag for this scenario */
  tag(): string {

    return this._tag;
  }

  /** @brief fetch the user logging MongoDB collection name */
  userLog(): string {

    return this._userLog;
  }

  /** @brief fetch the user logging MongoDB URL */
  userLogURL(): string {

    return this._userLogURL;
  }

  /** @brief generate the instances of trains that were in the simulation */
  generateTrains(): boolean {

    this.status('scanning generated trains...');

    if (typeof this._movements.generated_trains === 'undefined') {

      this.setError('can not find generated trains schema.');

      return true;
    }

    const fields: Map<string, number> = this.fieldMap(this._movements.generated_trains);

    /* make sure we can find everything */

    if (!fields || fields.size === 0) {

      this.setError('can not find generated trains schema.');

      return true;
    }

    if (!isset(this._movements.generated_trains.data) || !isarray(this._movements.generated_trains.data)) {

      this.setError('no generated trains data table.');

      return true;
    }

    const    gCol: number = (fields.get('generated_train_id') ?? 0) - 1;
    const    pCol: number = (fields.get('parent_train_id') ?? 0)    - 1;
    const nameCol: number = (fields.get('name') ?? 0)               - 1;

    let hits: number = 0;

    for (const row of this._movements.generated_trains.data) {

      let        instance: Train|null  = null;
      const    templateId: number = row[pCol];
      const   generatedId: number = row[gCol];
      const generatedName: string = row[nameCol];
      let        template: Train|null|undefined  = null;

      /* find the train template to use */

      if (this._actTrains.has(templateId)) {

        template = this._actTrains.get(templateId);

      } else if (this._evnTrains.has(templateId)) {

        template = this._evnTrains.get(templateId);

      } else if (this._expTrains.has(templateId)) {

        template = this._expTrains.get(templateId);

      } else if (this._schTrains.has(templateId)) {

        template = this._schTrains.get(templateId);

      } else if (this._wkyTrains.has(templateId)) {

        template = this._wkyTrains.get(templateId);
      }

      if (!template) {

        this.setError('could not find train template with id: ' + templateId);

        return false;
      }

      /* stamp out an instance (this is shallow copy) */

      instance = template.clone();

      /* deep copy anything that we might actually modify */

      instance.setGeneratedID(generatedId);
      instance.setGeneratedName(generatedName);

      /* save the generated train */

      this._simTrains.set(instance.generatedID(), instance);

      this._iidTrains.set(Train.iid(instance), instance);

      hits++;
    }

    this.status('generated ' + hits + ' simulation trains.');

    return true;
  }

  /** scan in the diamond crossing data (if we have it) */
  scanDiamondCrossings(): boolean {

    /* do we even have crossing data? */

    this.status('scanning diamond crossings...');

    if (!this._crossings) {

      this.status('no crossing data, skipping.');

      return true;
    }

    /* ok, scan it ... */

    {
      const fields: Map<string, number> = this.fieldMap(this._crossings);

      /* make sure we can find everything */

      if (!fields || fields.size === 0) {

        this.setError('can not find crossings fields in schema.');

        return true;
      }

      if (!isset(this._crossings.data) || !isarray(this._crossings.data)) {

        this.setError('no crossings data table.');

        return true;
      }

      const didCol: number = (fields.get('dp_id') ?? 0)            - 1;
      const   aCol: number = (fields.get('diamond_occupied') ?? 0) - 1;
      const   dCol: number = (fields.get('diamond_vacated') ?? 0)  - 1;

      let currentDP: DecisionPoint|null|undefined = null;
      let         dpid: number                    = -1;

      for (const row of this._crossings.data) {

        let aSec: number = 0;
        let dSec: number = 0;
        const did        = row[didCol];

        if (did != dpid) {

          /* have we changed decisions points? */

          const dp: DecisionPoint | undefined = this._dps.get(did);

          if (!dp) {

            this.setError(`no such decision point id (${did}) while scanning crossings.`);

            currentDP = null;
            dpid      = -1;

            continue;
          }

          currentDP = dp;
          dpid      = did;

        }

        if (!currentDP) {


          /* nothing we can do, can't establish which decision point this data is for */

          continue;
        }

        /* what's the arrival/departure for the crossing train... */

        if ((row[aCol] === undefined) || (row[aCol] === '')) {

          this.status('WARNING: missing/empty arrival time for crossing: ' + row[didCol]);

        } else {

          aSec = this.tzSeconds(Period.toSeconds(row[aCol]));
        }

        if ((row[dCol] === undefined) || (row[dCol] === '')) {

          this.status('WARNING: missing/empty departure time for crossing: ' + row[didCol]);

        } else {

          dSec = this.tzSeconds(Period.toSeconds(row[dCol]));
        }

        if (dSec < aSec) {

          /* Shark still looks fake. */

          dSec = aSec;
        }

        /** accumulate the segments... */

        if (!this._dpCrossings.has(currentDP._id)) {

          this._dpCrossings.set(currentDP._id, {segments: []});
        }

        this._dpCrossings.get(currentDP._id).segments.push([aSec, dSec]);

      }
    }

    this.status(`crossings gathered for ${this._dpCrossings.size} decision points.`)

    for (let [dpid, data] of this._dpCrossings) {

      const dp: DecisionPoint | undefined = this._dps.get(dpid);
      let label: string = dpid.toString();

      if (dp) {

        label = dp._name;
      }

      this.status(`. .  ${label} crossings=${data.segments.length}`);

    }

    /* at this point each of _simTrains has all of its data, and ready for plotting */

    this.status('crossing data scanned.');

    return true;
  }

  scanDelays(): boolean {

    if (!this._movements) {

      this.status(`WARNING: no train movements, skipping delay scanning.`);

      return true;
    }

    /*
     * note: we have to scan the train delay events at the very end, because some of the processing
     * requires knowledge about adjacent decision points or what tracks are linked to what other tracks.
     *
     */

    this.status('scanning movement delays (all trains)...');

    if (typeof this._movements.delays === 'undefined') {

      this.setError('can not find movement delays schema.');

      return true;
    }

    const fields: Map<string, number> = this.fieldMap(this._movements.delays);

    /* make sure we can find everything */

    if (!fields || fields.size === 0) {

      this.setError('can not find movement delays schema.');

      return true;
    }

    if (!isset(this._movements.delays.data) || !isarray(this._movements.delays.data)) {

      this.setError('no movement delays data table.');

      return true;
    }

    const       didCol: number = (fields.get('delay_id') ?? 0)     - 1;
    const       dptCol: number = (fields.get('datapoint_id') ?? 0) - 1;
    const         cCol: number = (fields.get('delay_code') ?? 0)   - 1;
    const         dCol: number = (fields.get('duration') ?? 0)     - 1;
    let           hits: number = 0;

    for (const row of this._movements.delays.data) {

      let dSec: number = 0;

      if ((row[dCol] === undefined) || (row[dCol] === '')) {

        this.status('WARNING: missing/empty duration for delay ' + row[didCol]);

      } else {

        dSec = Period.toSeconds(row[dCol]);
      }

      /* fetch raw data */

      const dlyid    = row[didCol];
      const did      = row[dptCol];
      const code     = row[cCol];
      const duration = dSec;

      /* find its movement */

      if (!this._moves.has(did)) {

        this.setError('no such data point (' + did + ') for delay with id: ' + dlyid + '.');

        continue;
      }

      const move: Movement|undefined = this._moves.get(did);

      if (!move) {

        continue;
      }

      /* create a delay (in hours) */

      const delay: MovementDelay = new MovementDelay(code, duration / 3600.0);

      /*
       * check for consist changes; for example train length or weight changed, we need to track these
       * so that when they eventually hover over some movement in the string line viewer, we can show
       * them the current train's consist, rather than just its original definition.
       *
       */

      if (MovementDelay.toDelayType(code) == DelayType.DELAY_TYPE_TRAIN_CONSIST) {

        /*
         * delay codes associate with specific movements, and the movement may or may not
         * be on a track, that is a valid stopping tracking for this train.  So, we look up
         * the consist by matching for the link/track id in the stops for this train.  Once
         * we find one, we take that as the match.
         *
         * we assume there are never multiple stop definitions per train per link/track. if
         * that ever happens we need to add something to the delay results from the simulator
         * to help us know which stop is the one to use.
         *
         */

        let stops: Stop[] = move._parent._train._stops;

        if (stops.length == 0) {

          this.status(`WARNING: delay ${dlyid} for move ${move._id} found but the train (#${move._parent._train._id}) has no stops.`);

          continue;
        }

        let consist: StopConsist|null = stops.filter(x => x._tracks.has(move._track._id)).map(x => x._change)[0] ?? null;

        if (!consist) {

          /* none of the stops for these matches the track this delay event is for */

          this.status(`WARNING: delay ${dlyid} for move ${move._id} has no matching stop track (${move._track._id}) for train #${move._parent._train._id}.`);

          continue ;
        }

        /* set the new consist for this train */

        move._consist = consist;

        move._parent._consistChanges.push({
          move,
          consist
        });

        this.status(` train #${move._parent._train._id} (${move._parent._train._generatedName}) has consist change at move ${move._id}, dp=${move._dp._name}.`);
      }

      /* save it! */

      move.add(delay);

      hits++;

    } /* end of delay harvesting */

    this.status('scanned ' + hits + ' movement delays.');

    return true;
  }

  setDefaultConsist(): boolean {

    if (!this._movements) {

      this.status(`WARNING: no train movements, skipping default consist build.`);

      return true;
    }

    this.status(`setting default consist for all train movements...`);

    /*
     * once any train consist changes have been found throughout the various train
     * movement histories, we do a final pass of all train movements to fill in the
     * current "effective" consist.
     *
     */

    let tCount: number = 0;
    let mCount: number = 0;

    for (const t of this._simTrains.values()) {

      /* walk the train movements (its history) */

      if (!t._history || !t._history._history) {

        continue ;
      }

      const         h: Movement[]    = t._history._history;

      if (!h || h.length < 2) {

        continue ;
      }

      /* set the default consist for this train */

      let defaultConsist: StopConsist = t._history._history[0]._consist;

      if (typeof t._history._history[0]._consist == 'undefined') {

        this.status(`WARNING: train ${t._id} : ${t._name}, does not appear to have an initial consist.`);

        continue ;
      }

      let oldMove: Movement|null  = null;

      for (const move of h) {

        if (oldMove == null) {

          oldMove = move;
        }

        /*
         * set the consist for this movement; if the movement already has a consist, then this was
         * a movement where there was a consist change, so we have to update the default consist
         * to use. Because the moves are ordered by datapoint_id they are ordered in the train's
         * own simulated movement history, and since we are walking from the start to the end...
         * we just keep reusing the current consist, and as it changes we update it.  This way
         * we can always have the "current" consist right in the movement.
         *
         * This avoids having to search backwards for the the most recent consist change.  We aren't
         * eating all the memory; because the StopConsist objects are used by reference here, we don't
         * clone.  Effectively we just reuse a pointer to the most recent StopConsist object.
         *
         */

        if (move._consist) {

          defaultConsist = move._consist;

        } else {

          move._consist = defaultConsist;
        }

        mCount++;

        /* advance... */

        oldMove = move;

      } /* end of move walk for a single train */

      tCount++;

    } /* end of train walk */

    this.status(`consist has been set for ${mCount} movements, across ${tCount} trains`);

    return true;
  }

  /** @brief gather up the movement data for trains */
  buildMovementHistory(): boolean {

    if (!this._movements) {

      this.status(`WARNING: no train movements, skipping movement history build.`);

      return true;
    }

    /*
     * first gather the data-points as the movement history for each train, and cache the movements
     * by id, so that we easily add the delay data to each one in the second pass
     *
     */

    this.status('scanning movement data-points (all trains)...');

    const datapoints: Map<number, Movement> = new Map<number, Movement>();

    {
      if (typeof this._movements.datapoints === 'undefined') {

        this.setError('can not find movement data-points schema.');

        return true;
      }

      const fields: Map<string, number> = this.fieldMap(this._movements.datapoints);

      /* make sure we can find everything */

      if (!fields || fields.size === 0) {

        this.setError('can not find movement datapoints schema.');

        return true;
      }

      if (!isset(this._movements.datapoints.data) || !isarray(this._movements.datapoints.data)) {

        this.setError('no movement datapoints data table.');

        return true;
      }

      const  didCol: number = (fields.get('datapoint_id') ?? 0)       - 1;
      const  gidCol: number = (fields.get('generated_train_id') ?? 0) - 1;
      const dpidCol: number = (fields.get('dp_id') ?? 0)              - 1;
      const  lidCol: number = (fields.get('link_id') ?? 0)            - 1;
      const   atCol: number = (fields.get('arrival_time') ?? 0)       - 1;
      const   dtCol: number = (fields.get('departure_time') ?? 0)     - 1;

      let currentTrain: Train|null|undefined = null;
      let         ctid: number     = -1;

      for (const row of this._movements.datapoints.data) {

        let aSec: number = 0;
        let dSec: number = 0;

        if ((row[atCol] === undefined) || (row[atCol] === '')) {

          this.status('WARNING: missing/empty arrival time for movement: ' + row[didCol]);

        } else {

          aSec = this.tzSeconds(Period.toSeconds(row[atCol]));
        }

        if ((row[dtCol] === undefined) || (row[dtCol] === '')) {

          this.status('WARNING: missing / empty departure time for movement: ' + row[didCol]);

        } else {

          dSec = this.tzSeconds(Period.toSeconds(row[dtCol]));
        }

        /* fetch raw data */

        const nid       = row[didCol];
        const tid       = row[gidCol];
        const did       = row[dpidCol];
        const lid       = row[lidCol];
        const arrival   = aSec;
        let   departure = dSec;

        if (departure < arrival) {

          /* Shark still looks fake. */

          departure = arrival;
        }

        /* lookup sub-objects */

        if (!this._dps.has(did)) {

          this.setError(`no such decision point id (${did}) while scanning datapoints.`);

          continue;
        }

        const dp: DecisionPoint|undefined = this._dps.get(did);

        if (!dp || !this._links.has(lid)) {

          this.setError(`no such decision point id (${did}) while scanning datapoints.`);

          continue;
        }

        const track: Track|undefined = this._links.get(lid);

        if (!track) {

          this.setError(`no such link (${lid}) while scanning datapoints.`);

          continue;
        }

        /* what train are adding data to? */

        if (ctid !== tid) {

          if (!this._simTrains.has(tid)) {

            this.setError(`no such generated train id (${tid}) while scanning datapoints.`);

            continue;
          }

          currentTrain = this._simTrains.get(tid);
          ctid         = tid;
        }

        /* create/cache new movement */

        if (!currentTrain) {

          this.setError(`no such train (${tid}) while scanning datapoints.`);

          continue;
        }

        /* add to train history */

        const move: Movement = new Movement(
          currentTrain._history,
          nid,
          dp,
          track,
          arrival,
          departure);

        currentTrain._history?.add(move);

        this._moves.set(nid, move);

        if (currentTrain._history && currentTrain._history._tMin < this._tMin) {

          this._tMin = currentTrain._history._tMin;
        }

        if (currentTrain._history && currentTrain._history._tMax > this._tMax) {

          this._tMax = currentTrain._history._tMax;
        }

        if (currentTrain._history && currentTrain._history._dMin < this._dMin) {

          this._dMin = currentTrain._history._dMin;
        }

        if (currentTrain._history && currentTrain._history._dMax > this._dMax) {

          this._dMax = currentTrain._history._dMax;
        }

        /* temporarily cache it, so we can add delays in second pass */

        datapoints.set(nid, move);

      } /* end of data point harvesting */
    }

    this.status('scanned ' + datapoints.size + ' data points.');

    this.status('time ordering movement histories...');

    /* go through all the train movements and ensure they are sorted in increasing time order */

    for (const t of this._simTrains.values()) {

      t._history?._history.sort((a: Movement, b: Movement): number => {

        /*
         * normally we would order datapoint by mileage, but then you have to check, which
         * direction is the train moving in, to know which way you are sorting.  So...sort
         * on time...still no joy, some interlinks can have 0 run-time (its allowed).  So
         * that will give you a random sort.
         *
         * Plus, just to make things fun, trains can turn!  So even if you tried to use
         * distance, you can't, because trains might be turning back and forth.  Instead,
         * use the datapoint_id which we know is just an increasing integer.  That way
         * we don't care about the time value or the mileage value.
         *
         * @note linesim was updated to output data points in the exact order they were
         * generated in.  So the lowest data point id is the first one.
         *
         */

        if (a._id < b._id) {

          return -1;

        } else if (a._id > b._id) {

          return 1;

        }

        return 0;

      });
    }

    /* build the quad tree, this allows us to find movements knowing just a nearby time/mileage */

    /* @note tmin/tmax/dmin/dmax has the total bounding box to use for the entire tree */

    this.status('building quad tree...');

    this._quad = new QuadTree(
      null,
      0,
      this._tMin,
      this._dMin,
      this._tMax - this._tMin,
      this._dMax - this._dMin);

    for (const t of this._simTrains.values()) {

      /* walk the train movements (its history) */

      if (!t._history || !t._history._history) {

        continue ;
      }

      const         h: Movement[]    = t._history._history;

      if (!h || h.length < 2) {

        continue ;
      }

      /* set the default consist for this train */

      t._history._history[0]._consist = new StopConsist(
        -1,
        t._tc,
        t._tonnage,
        t._length,
        t._priority,
        t._overtake,
        -1);

      t._history._consistChanges.push({
        move:    t._history._history[0],
        consist: t._history._history[0]._consist
      });

      let oldMove: Movement|null  = null;
      let dp1: DecisionPoint|null = h[0]._dp;
      let dp2: DecisionPoint|null = h[h.length - 1]._dp ?? null;

      const westBound: boolean    = (dp1 ? dp1._mileage : 0) < (dp2 ? dp2._mileage:  0);

      for (const move of h) {

        // console.log("oid: " + move.objId() + ' - ' + move._dp._name + ' : ' + move._dp._mileage);

        /*
         * initialize the bounding box for this movement, from mileage/departure of last
         * movement, to the mileage departure of this movement.
         *
         */

        if (oldMove == null) {

          oldMove = move;
        }

        if (westBound) {

          move._qtX      = oldMove._departure;
          move._qtY      = move._dp ? move._dp._mileage : 0;
          move._qtWidth  = move._departure - oldMove._departure;
          move._qtHeight = (oldMove._dp ? oldMove._dp._mileage : 0) - (move._dp ? move._dp._mileage : 0);

        } else {

          move._qtX      = oldMove._departure;
          move._qtY      = oldMove._dp ? oldMove._dp._mileage : 0;
          move._qtWidth  = move._departure - oldMove._departure;
          move._qtHeight = (move._dp ? move._dp._mileage : 0) - (oldMove._dp ? oldMove._dp._mileage : 0);

        }

        if (oldMove._id === move._id) {

          move._qtWidth  = move._departure - oldMove._arrival;
          move._qtX      = oldMove._arrival;
        }

        if (move._qtHeight < 0) {

          /* it's an eastbound train, mileage is decreasing, we have to flip the bounding box */

          move._qtY        = move._qtY + move._qtHeight;
          move._qtHeight   = - move._qtHeight;
        }

        /* add movement to quad tree */

        this._quad.insert(move);

        /*
         * chain the movemements together in order; the movement history of each train is already
         * sorted by datapoint_id; which means they are in the actual simulated order, the very
         * first one with the lowest datapoint_id is the first train move.  So, as we walk this
         * history in order, just link them with previous/next pointers.
         *
         */

        if (oldMove) {

          move._previous = oldMove;
        }

        oldMove._next = move;

        /* advance... */

        oldMove = move;

      } /* end of move walk for a single train */

      this.status(`. quad tree ready for train ${t._name} : ${t._id} : ${t._generatedId}`);

    } /* end of train walk */

    this.status('train movements indexed (n: ' + this._quad.size() + ' height: ' + this._quad.height() +  ').');

    /* at this point each of _simTrains has all of its data, and ready for plotting */

    this.status('all movement data scanned.');

    return true;
  }

  /** @brief read in the actual trains */
  scanActualTrains(): boolean {

    this.status('scanning actual trains...');

    if (typeof this._plant.trains.train_actual === 'undefined') {

      this.setError('can not find actual trains schema.');

      return true;
    }

    const fields: Map<string, number> = this.fieldMap(this._plant.trains.train_actual);

    /* make sure we can find everything */

    if (fields.size === 0) {

      this.setError('can not find actual trains schema.');

      return true;
    }

    if (!isset(this._plant.trains.train_actual.data) || !isarray(this._plant.trains.train_actual.data)) {

      this.setError('no actual trains data table.');

      return true;
    }

    // noinspection DuplicatedCode
    const   idCol: number = (fields.get('train_id') ?? 0)       - 1;
    const nameCol: number = (fields.get('name') ?? 0)           - 1;
    const  tidCol: number = (fields.get('train_class_id') ?? 0) - 1;
    const fromCol: number = (fields.get('link_from') ?? 0)      - 1;
    const   toCol: number = (fields.get('link_to') ?? 0)        - 1;
    const  tonCol: number = (fields.get('tonnage') ?? 0)        - 1;
    const  lenCol: number = (fields.get('length') ?? 0)         - 1;
    const  priCol: number = (fields.get('priority') ?? 0)       - 1;
    let    maxCol: number = -1;

    if (fields.has('max_overtake_delay')) {

      maxCol = (fields.get('max_overtake_delay') ?? 0)          - 1;

    } else if (fields.has('overtake_delay_limit')) {

      maxCol = (fields.get('overtake_delay_limit') ?? 0)        - 1;
    }

    const depCol: number = (fields.get('departure_time') ?? 0)  - 1;
    let     hits: number = 0;

    for (const row of this._plant.trains.train_actual.data) {

      /* figure all the possible paths (combinations of from/to */

      const paths: Track[][]  = [];
      const name:  string     = row[nameCol];
      let    dep:  number     = 0;

      if ((row[depCol] === undefined) || (row[depCol] === '')) {

        this.status('WARNING: missing/empty departure time for actual train: ' + name);

      } else {

        dep = this.tzSeconds(Period.toSeconds(row[depCol]));
      }

      if ((row[fromCol].length === 0) || (row[toCol].length === 0)) {

        this.status('ERROR: actual train is missing origin or destination link: ' + name + ' (' + row[idCol] + ')');

      } else {

        for (const from_id of row[fromCol]) {

          for (const to_id of row[toCol]) {

            if (!this._links.has(from_id)) {

              this.status('WARNING: missing from link: ' + from_id);

              continue;
            }

            if (!this._links.has(to_id)) {

              this.status('WARNING: missing to link: ' + to_id);

              continue;
            }

            const src: Track|undefined = this._links.get(from_id);
            const dst: Track|undefined = this._links.get(to_id);

            if (src && dst) {

              paths.push([src, dst]);
            }
          }
        }
      }

      /* save the train */

      let trainClass: TrainClass|undefined = this._trainClasses.get(row[tidCol]);

      if (!trainClass) {

        this.status(`WARNING: (actual train: ${row[idCol]} : ${row[nameCol]})  missing/undefined train class ID: '${row[tidCol]}', no such train class ID.`);

        trainClass = this._trainClasses.get(this.missingTrainClass);

      }

      if (trainClass) {

        this._actTrains.set(row[idCol], new ActualTrain(
          row[idCol],
          row[nameCol],
          paths,
          trainClass,
          row[tonCol],
          row[lenCol],
          row[priCol],
          row[maxCol],
          dep,
          hits
        ));
      }

      hits++;
    }

    this.status('scanned ' + hits + ' actual trains');

    return true;
  }

  /** @brief read in the even trains */
  scanEvenTrains(): boolean {

    this.status('scanning even trains...');

    if (typeof this._plant.trains.train_even === 'undefined') {

      this.setError('can not find even trains schema.');

      return true;
    }

    const fields: Map<string, number> = this.fieldMap(this._plant.trains.train_even);

    /* make sure we can find everything */

    if (fields.size === 0) {

      this.setError('can not find even trains schema.');

      return true;
    }

    if (!isset(this._plant.trains.train_even.data) || !isarray(this._plant.trains.train_even.data)) {

      this.setError('no even trains data table.');

      return true;
    }

    // noinspection DuplicatedCode
    const   idCol: number = (fields.get('train_id') ?? 0)           - 1;
    const nameCol: number = (fields.get('name') ?? 0)               - 1;
    const  tidCol: number = (fields.get('train_class_id') ?? 0)     - 1;
    const fromCol: number = (fields.get('link_from') ?? 0)          - 1;
    const   toCol: number = (fields.get('link_to') ?? 0)            - 1;
    const  tonCol: number = (fields.get('tonnage') ?? 0)            - 1;
    const  lenCol: number = (fields.get('length') ?? 0)             - 1;
    const  priCol: number = (fields.get('priority') ?? 0)           - 1;
    let    maxCol: number = -1;

    if (fields.has('max_overtake_delay')) {

      maxCol = (fields.get('max_overtake_delay') ?? 0) - 1;

    } else if (fields.has('overtake_delay_limit')) {

      maxCol = (fields.get('overtake_delay_limit') ?? 0) - 1;
    }

    /* in some cases they provide trains per day, but in some cases per week */

    let perDiv: number = 1;
    let perCol: number = -1;

    if (fields.has('trains_per_day')) {

      perCol = (fields.get('trains_per_day') ?? 0) - 1;

    } else if (fields.has('trains_per_week')) {

      perCol = (fields.get('trains_per_week') ?? 0) - 1;
      perDiv = 7;
    }

    const  stdCol: number = (fields.get('stdev') ?? 0)             - 1;
    const seedCol: number = (fields.get('seed') ?? 0)              - 1;
    let    depCol: number = (fields.get('departure_time') ?? 0)    - 1;
    let      hits: number = 0;

    if (fields.get('time_of_day')) {

      depCol = (fields.get('time_of_day') ?? 0) - 1;
    }

    for (const row of this._plant.trains.train_even.data) {

      /* figure all the possible paths (combinations of from/to */

      const paths         = [];
      const  name: string = row[nameCol];
      let     dep: number = 0;

      if ((row[depCol] === undefined) || (row[depCol] === '')) {

        this.status('WARNING: missing/empty departure time for even train: ' + name);

      } else {

        dep = this.tzSeconds(Period.toSeconds(row[depCol]));
      }

      if ((row[fromCol].length === 0) || (row[toCol].length === 0)) {

        this.status('ERROR: even train is missing origin or destination link: ' + name + ' (' + row[idCol] + ')');

      } else {

        for (const from_id of row[fromCol]) {

          for (const to_id of row[toCol]) {

            if (!this._links.has(from_id)) {

              this.status('WARNING: missing from link: ' + from_id);
              continue;
            }

            if (!this._links.has(to_id)) {

              this.status('WARNING: missing to link: ' + to_id);
              continue;
            }

            const src: Track|undefined = this._links.get(from_id);
            const dst: Track|undefined = this._links.get(to_id);

            if (src && dst) {

              paths.push([src, dst]);
            }
          }
        }
      }

      /* save the train */

      let trainClass: TrainClass|undefined = this._trainClasses.get(row[tidCol]);

      if (!trainClass) {

        this.status(`WARNING: (even train: ${row[idCol]} : ${row[nameCol]}) missing/undefined train class ID: '${row[tidCol]}', no such train class ID.`);

        trainClass = this._trainClasses.get(this.missingTrainClass);
      }

      if (trainClass) {
        this._evnTrains.set(row[idCol], new EvenTrain(
          row[idCol],
          row[nameCol],
          paths,
          trainClass,
          row[tonCol],
          row[lenCol],
          row[priCol],
          row[maxCol],
          row[perCol] / perDiv,
          row[stdCol],
          row[seedCol],
          dep,
          hits
        ));
      }

      hits++;

    }

    this.status('scanned ' + hits + ' even trains');

    return true;
  }

  /** @brief read in the exponential trains */
  scanExponentialTrains(): boolean {

    this.status('scanning exponential trains...');

    if (typeof this._plant.trains.train_exponential === 'undefined') {

      this.setError('can not find exponential trains schema.');

      return true;
    }

    const fields: Map<string, number> = this.fieldMap(this._plant.trains.train_exponential);

    /* make sure we can find everything */

    if (fields.size === 0) {

      this.setError('can not find exponential trains schema.');

      return true;
    }

    if (!isset(this._plant.trains.train_exponential.data) || !isarray(this._plant.trains.train_exponential.data)) {

      this.setError('no exponential trains data table.');

      return true;
    }

    const   idCol: number = (fields.get('train_id') ?? 0)           - 1;
    const nameCol: number = (fields.get('name') ?? 0)               - 1;
    const  tidCol: number = (fields.get('train_class_id') ?? 0)     - 1;
    const fromCol: number = (fields.get('link_from') ?? 0)          - 1;
    const   toCol: number = (fields.get('link_to') ?? 0)            - 1;
    const  tonCol: number = (fields.get('tonnage') ?? 0)            - 1;
    const  lenCol: number = (fields.get('length') ?? 0)             - 1;
    const  priCol: number = (fields.get('priority') ?? 0)           - 1;
    let    maxCol: number = -1;

    if (fields.has('max_overtake_delay')) {

      maxCol = (fields.get('max_overtake_delay') ?? 0) - 1;

    } else if (fields.has('overtake_delay_limit')) {

      maxCol = (fields.get('overtake_delay_limit') ?? 0) - 1;
    }

    /* in some cases they provide trains per day, but in some cases per week */

    let perDiv: number = 1;
    let perCol: number = -1;

    if (fields.has('trains_per_day')) {

      perCol = (fields.get('trains_per_day') ?? 0) - 1;

    } else if (fields.has('trains_per_week')) {

      perCol = (fields.get('trains_per_week') ?? 0) - 1;
      perDiv = 7;
    }

    const seedCol: number = (fields.get('seed') ?? 0)               - 1;
    let      hits: number = 0;

    for (const row of this._plant.trains.train_exponential.data) {

      /* figure all the possible paths (combinations of from/to */

      const paths         = [];
      const  name: string = row[nameCol];

      if ((row[fromCol].length === 0) || (row[toCol].length === 0)) {

        this.status('ERROR: exponential train is missing origin or destination link: ' + name + ' (' + row[idCol] + ')');

      } else {

        for (const from_id of row[fromCol]) {

          for (const to_id of row[toCol]) {

            if (!this._links.has(from_id)) {

              this.status('WARNING: missing from link: ' + from_id);
              continue;
            }

            if (!this._links.has(to_id)) {

              this.status('WARNING: missing to link: ' + to_id);
              continue;
            }

            const src: Track|undefined = this._links.get(from_id);
            const dst: Track|undefined = this._links.get(to_id);

            if (src && dst) {

              paths.push([src, dst]);
            }
          }
        }
      }

      /* save the train */

      let trainClass: TrainClass|undefined = this._trainClasses.get(row[tidCol]);

      if (!trainClass) {

        this.status(`WARNING: (exponential train: ${row[idCol]} : ${row[nameCol]})  missing/undefined train class ID: '${row[tidCol]}', no such train class ID.`);

        trainClass = this._trainClasses.get(this.missingTrainClass);
      }

      if (trainClass) {

        this._expTrains.set(row[idCol], new ExponentialTrain(
          row[idCol],
          row[nameCol],
          paths,
          trainClass,
          row[tonCol],
          row[lenCol],
          row[priCol],
          row[maxCol],
          row[perCol] / perDiv,
          row[seedCol],
          hits
        ));
      }

      hits++;
    }

    this.status('scanned ' + hits + ' exponential trains');

    return true;
  }

  /** @brief read in the scheduled trains */
  scanScheduledTrains(): boolean {

    this.status('scanning scheduled trains...');

    if (typeof this._plant.trains.train_scheduled === 'undefined') {

      this.setError('can not find scheduled trains schema.');

      return true;
    }

    const fields: Map<string, number> = this.fieldMap(this._plant.trains.train_scheduled);

    /* make sure we can find everything */

    if (fields.size === 0) {

      this.setError('can not find scheduled trains schema.');

      return true;
    }

    if (!isset(this._plant.trains.train_scheduled.data) || !isarray(this._plant.trains.train_scheduled.data)) {

      this.setError('no scheduled trains data table.');

      return true;
    }

    const   idCol: number = (fields.get('train_id') ?? 0)           - 1;
    const nameCol: number = (fields.get('name') ?? 0)               - 1;
    const  tidCol: number = (fields.get('train_class_id') ?? 0)     - 1;
    const fromCol: number = (fields.get('link_from') ?? 0)          - 1;
    const   toCol: number = (fields.get('link_to') ?? 0)            - 1;
    const  tonCol: number = (fields.get('tonnage') ?? 0)            - 1;
    const  lenCol: number = (fields.get('length') ?? 0)             - 1;
    const  priCol: number = (fields.get('priority') ?? 0)           - 1;
    let    maxCol: number = -1;

    if (fields.has('max_overtake_delay')) {

      maxCol = (fields.get('max_overtake_delay') ?? 0) - 1;

    } else if (fields.has('overtake_delay_limit')) {

      maxCol = (fields.get('overtake_delay_limit') ?? 0) - 1;
    }

    const daysCol: number = (fields.get('days_of_week') ?? 0)       - 1;
    const  perCol: number = (fields.get('percent_early') ?? 0)      - 1;
    const  sdeCol: number = (fields.get('stdev_early') ?? 0)        - 1;
    const  sdlCol: number = (fields.get('stdev_late') ?? 0)         - 1;
    const seedCol: number = (fields.get('seed') ?? 0)               - 1;
    const  depCol: number = (fields.get('departure_time') ?? 0)     - 1;
    let      hits: number = 0;

    for (const row of this._plant.trains.train_scheduled.data) {

      /* figure all the possible paths (combinations of from/to */

      const paths         = [];
      const  name: string = row[nameCol];
      let     dep: number = 0;

      if ((row[depCol] === undefined) || (row[depCol] === '')) {

        this.status('WARNING: missing/empty departure time for scheduled train: ' + name);

      } else {

        dep = this.tzSeconds(Period.toSeconds(row[depCol]));
      }

      if ((row[fromCol].length === 0) || (row[toCol].length === 0)) {

        this.status('ERROR: scheduled train is missing origin or destination link: ' + name + ' (' + row[idCol] + ')');

      } else {

        for (const from_id of row[fromCol]) {

          for (const to_id of row[toCol]) {

            if (!this._links.has(from_id)) {

              this.status('WARNING: missing from link: ' + from_id);

              continue;
            }

            if (!this._links.has(to_id)) {

              this.status('WARNING: missing to link: ' + to_id);

              continue;
            }

            const src: Track|undefined = this._links.get(from_id);
            const dst: Track|undefined = this._links.get(to_id);

            if (src && dst) {

              paths.push([src, dst]);
            }
          }
        }
      }

      /* save the train */

      let trainClass: TrainClass|undefined = this._trainClasses.get(row[tidCol]);

      if (!trainClass) {

        this.status(`WARNING: (scheduled train: ${row[idCol]} : ${row[nameCol]})  missing/undefined train class ID: '${row[tidCol]}', no such train class ID.`);

        trainClass = this._trainClasses.get(this.missingTrainClass);
      }

      if (trainClass) {

        this._schTrains.set(row[idCol], new ScheduledTrain(
          row[idCol],
          row[nameCol],
          paths,
          trainClass,
          row[tonCol],
          row[lenCol],
          row[priCol],
          row[maxCol],
          row[daysCol],
          row[perCol],
          row[sdeCol],
          row[sdlCol],
          row[seedCol],
          dep,
          hits
        ));
      }

      hits++;
    }

    this.status('scanned ' + hits + ' scheduled trains');

    return true;
  }

  /** @brief read in the scheduled trains */
  scanWeeklyTrains(): boolean {

    this.status('scanning weekly trains...');

    if (typeof this._plant.trains.train_weekly === 'undefined') {

      this.setError('can not find weekly trains schema.');

      return true;
    }

    const fields: Map<string, number> = this.fieldMap(this._plant.trains.train_weekly);

    /* make sure we can find everything */

    if (fields.size === 0) {

      this.setError('can not find weekly trains schema.');

      return true;
    }

    if (!isset(this._plant.trains.train_weekly.data) || !isarray(this._plant.trains.train_weekly.data)) {

      this.setError('no weekly trains data table.');

      return true;
    }

    const   idCol: number = (fields.get('train_id') ?? 0)              - 1;
    const nameCol: number = (fields.get('name') ?? 0)                  - 1;
    const  tidCol: number = (fields.get('train_class_id') ?? 0)        - 1;
    const fromCol: number = (fields.get('link_from') ?? 0)             - 1;
    const   toCol: number = (fields.get('link_to') ?? 0)               - 1;
    const  tonCol: number = (fields.get('tonnage') ?? 0)               - 1;
    const  lenCol: number = (fields.get('length') ?? 0)                - 1;
    const  priCol: number = (fields.get('priority') ?? 0)              - 1;
    const   pwCol: number = (fields.get('trains_per_week') ?? 0)       - 1;
    const    sCol: number = (fields.get('seed') ?? 0)                  - 1;
    const    dCol: number = (fields.get('distribution_table_id') ?? 0) - 1;
    let    maxCol: number = -1;

    if (fields.has('max_overtake_delay')) {

      maxCol = (fields.get('max_overtake_delay') ?? 0) - 1;

    } else if (fields.has('overtake_delay_limit')) {

      maxCol = (fields.get('overtake_delay_limit') ?? 0) - 1;
    }

    let hits: number = 0;

    for (const row of this._plant.trains.train_weekly.data) {

      /* figure all the possible paths (combinations of from/to */

      const paths         = [];
      const  name: string = row[nameCol];

      if ((row[fromCol].length === 0) || (row[toCol].length === 0)) {

        this.status('ERROR: weekly train is missing origin or destination link: ' + name + ' (' + row[idCol] + ')');

      } else {

        for (const from_id of row[fromCol]) {

          for (const to_id of row[toCol]) {

            if (!this._links.has(from_id)) {

              this.status('WARNING: missing from link: ' + from_id);
              continue;
            }

            if (!this._links.has(to_id)) {

              this.status('WARNING: missing to link: ' + to_id);
              continue;
            }

            const src: Track|undefined = this._links.get(from_id);
            const dst: Track|undefined = this._links.get(to_id);

            if (src && dst) {

              paths.push([src, dst]);
            }
          }
        }
      }

      /* save the train */

      let trainClass: TrainClass|undefined = this._trainClasses.get(row[tidCol]);

      if (!trainClass) {

        this.status(`WARNING: (weekly train: ${row[idCol]} : ${row[nameCol]})  missing/undefined train class ID: '${row[tidCol]}', no such train class ID.`);

        trainClass = this._trainClasses.get(this.missingTrainClass);
      }

      if (trainClass) {

        this._wkyTrains.set(row[idCol], new WeeklyTrain(
          row[idCol],
          row[nameCol],
          paths,
          trainClass,
          row[tonCol],
          row[lenCol],
          row[priCol],
          row[maxCol],
          row[dCol],
          row[pwCol],
          row[sCol],
          hits
        ));
      }

      hits++;
    }

    this.status('scanned ' + hits + ' weekly trains');

    return true;
  }

  /** @brief read in the consist changes (if any) that get used by Stop objects */
  scanStopConsist(): boolean {

    this.status('scanning consist changes...');

    if (typeof this._plant.stop_consist_changes === 'undefined') {

      this.setError('can not find stop consist schema (outer).');

      return true;
    }

    if (typeof this._plant.stop_consist_changes.stop_consist_change === 'undefined') {

      this.setError('can not find stop consist schema (inner).');

      return true;
    }

    const fields: Map<string, number> = this.fieldMap(this._plant.stop_consist_changes.stop_consist_change);

    /* make sure we can find everything */

    if (fields.size === 0) {

      this.setError('can not find stop consist schema.');

      return true;
    }

    if (!isset(this._plant.stop_consist_changes.stop_consist_change.data) ||
        !isarray(this._plant.stop_consist_changes.stop_consist_change.data)) {

      this.setError('no stop consist data table.');

      return true;
    }

    const  idCol: number = (fields.get('consist_change_id') ?? 0) - 1;
    const  tcCol: number = (fields.get('train_class_id') ?? 0)    - 1;
    const tonCol: number = (fields.get('tonnage') ?? 0)           - 1;
    const lenCol: number = (fields.get('length') ?? 0)            - 1;
    const priCol: number = (fields.get('priority') ?? 0)          - 1;
    let   maxCol: number = -1;

    if (fields.has('max_overtake_delay')) {

      maxCol = (fields.get('max_overtake_delay') ?? 0) - 1;

    } else if (fields.has('overtake_delay_limit')) {

      maxCol = (fields.get('overtake_delay_limit') ?? 0) - 1;
    }

    let hits: number = 0;

    for (const row of this._plant.stop_consist_changes.stop_consist_change.data) {

      /* figure all the possible paths (combinations of from/to */

      let trainClass: TrainClass|undefined = this._trainClasses.get(row[tcCol]);

      if (!trainClass) {

        this.status(`WARNING: (stop consist: ${row[idCol]}) missing/undefined train class ID: '${row[tcCol]}', no such train class ID.`);

        trainClass = this._trainClasses.get(this.missingTrainClass);
      }

      if (trainClass) {

        this._stopConsist.set(row[idCol], new StopConsist(
          row[idCol],
          trainClass,
          row[tonCol],
          row[lenCol],
          row[priCol],
          row[maxCol],
          hits));
      }

      hits++;
    }

    this.status('scanned ' + hits + ' stop consist changes');

    return true;
  }

  /** @brief helper to look up a track by ID */
  trackById(lid: number): Track|null {

    return this._links.get(lid) ?? null;
  }

  /** @brief helper to look up a train by ID */
  trainById(tid: number): Train|null {

    /* we have to figure out which kind of train it is. */

    if (this._actTrains.has(tid)) {

      return this._actTrains.get(tid) ?? null;

    } else if (this._wkyTrains.has(tid)) {

      return this._wkyTrains.get(tid) ?? null;

    } else if (this._schTrains.has(tid)) {

      return this._schTrains.get(tid) ?? null;

    } else if (this._evnTrains.has(tid)) {

      return this._evnTrains.get(tid) ?? null;

    } else if (this._expTrains.has(tid)) {

      return this._expTrains.get(tid) ?? null;
    }

    return null;
  }

  /** @brief read in the train stops */
  scanStops(): boolean {

    this.status('scanning train stops...');

    if (typeof this._plant.train_stops === 'undefined') {

      this.setError('can not find train stop schema (outer).');

      return true;
    }

    if (typeof this._plant.train_stops.train_stop === 'undefined') {

      this.setError('can not find train stop schema (inner).');

      return true;
    }

    const fields: Map<string, number> = this.fieldMap(this._plant.train_stops.train_stop);

    /* make sure we can find everything */

    if (fields.size === 0) {

      this.setError('can not find train stop schema.');

      return true;
    }

    if (!isset(this._plant.train_stops.train_stop.data) ||
        !isarray(this._plant.train_stops.train_stop.data)) {

      this.setError('no train stop data table.');

      return true;
    }

    const     idCol: number = (fields.get('stop_id') ?? 0)                 - 1;
    const   nameCol: number = (fields.get('name') ?? 0)                    - 1;
    const    tidCol: number = (fields.get('train_id') ?? 0)                - 1;
    const    solCol: number = (fields.get('stop_on_link') ?? 0)            - 1;
    const    seqCol: number = (fields.get('sequence') ?? 0)                - 1;
    const     cmCol: number = (fields.get('clears_main') ?? 0)             - 1;
    const departCol: number = (fields.get('earliest_departure_time') ?? 0) - 1;
    const  linksCol: number = (fields.get('valid_links') ?? 0)             - 1;
    const   ccidCol: number = (fields.get('consist_change_id') ?? 0)       - 1;
    let        hits: number = 0;

    for (const row of this._plant.train_stops.train_stop.data) {

      /* create the stop */

      const stop: Stop = new Stop(
        row[idCol],
        row[nameCol],
        row[tidCol],
        row[departCol],
        row[cmCol],
        row[seqCol],
        row[solCol],
        null,
        hits
      );

      /* convert train id to a train (and link stop to train) ... */

      const train: Train|null  = this.trainById(row[tidCol]);

      if (!train) {

        this.setError('can\'t find train with id: ' + row[tidCol]);

        hits++;

        continue;
      }

      train._stops.push(stop);

      /* save the stop for later reference (stop events) */

      this._stops.set(stop._id, stop);

      /* convert valid links to links */

      if (!isset(row[linksCol]) || !isarray(row[linksCol])) {

        this.setError('stop (' + stop._id + ') has no links.');

        hits++;
        continue;
      }

      for (const linkId of row[linksCol]) {

        let  track: Track|null  = null;

        if (!this._links.has(linkId)) {

          this.setError('stop (' + stop._id + ') refers to invalid link: ' + linkId);

          continue;
        }

        track = this._links.get(linkId) ?? null;

        if (!track) {

          this.setError(`stop (${linkId}) null track. skipping.`);

          continue;
        }

        /* save the track into the stop */

        stop._tracks.set(linkId, track);

      } /* end of valid link walk */

      /* find the stop consist if applicable */

      const consistId = row[ccidCol];

      if (this._stopConsist.has(consistId)) {

        stop._change = this._stopConsist.get(consistId) ?? null;
      }

      hits++;
    }

    this.status('scanned ' + hits + ' train stop');

    return true;
  }

  scanStopEvents(): boolean {

    this.status('scanning train stop activities...');

    if (typeof this._plant.train_stop_events === 'undefined') {

      this.setError('can not find train stop activities schema (outer).');

      return true;
    }

    if (typeof this._plant.train_stop_events.train_stop_event === 'undefined') {

      this.setError('can not find train stop activities schema (inner).');

      return true;
    }

    const fields: Map<string, number> = this.fieldMap(this._plant.train_stop_events.train_stop_event);

    /* make sure we can find everything */

    if (fields.size === 0) {

      this.setError('can not find train stop activities schema.');

      return true;
    }

    if (!isset(this._plant.train_stop_events.train_stop_event.data) ||
        !isarray(this._plant.train_stop_events.train_stop_event.data)) {

      this.setError('no train stop activity data table.');

      return true;
    }

    const  sidCol: number = (fields.get('stop_id') ?? 0)               - 1;
    const   stCol: number = (fields.get('stop_type') ?? 0)             - 1;
    const  posCol: number = (fields.get('probability_of_stop') ?? 0)   - 1;
    const   psCol: number = (fields.get('probability_short') ?? 0)     - 1;
    const  minCol: number = (fields.get('min_stop_time') ?? 0)         - 1;
    const meanCol: number = (fields.get('mean_stop_time') ?? 0)        - 1;
    const   lsCol: number = (fields.get('stdev_long_stop_time') ?? 0)  - 1;
    const   ssCol: number = (fields.get('stdev_short_stop_time') ?? 0) - 1;
    let      hits: number = 0;

    for (const row of this._plant.train_stop_events.train_stop_event.data) {

      let stop: Stop|null = null;

      if (this._stops.has(row[sidCol])) {

        stop = this._stops.get(row[sidCol]) ?? null;

      } else {

        this.setError('stop event for invalid stop id: ' + row[sidCol]);

        continue;
      }

      if (stop) {

        stop._events.push(new StopEvent(
          StopEvent.typeFromString(row[stCol]),
          row[posCol],
          row[psCol],
          row[minCol],
          row[meanCol],
          row[lsCol],
          row[ssCol],
          hits
        ));
      }

      hits++;
    }

    this.status('scanned ' + hits + ' stop activities');

    return true;
  }

  scanTSOSpeeds(): boolean {

    this.status('scanning TSO (tso_speeds)...');

    if ((typeof this._plant.slow_orders === 'undefined') ||
        (typeof this._plant.slow_orders.tso_speeds === 'undefined')) {

      this.setError('can not find tso_speeds schema.');

      return true;
    }

    const fields: Map<string, number> = this.fieldMap(this._plant.slow_orders.tso_speeds);

    /* make sure we can find everything */

    if (fields.size === 0) {

      this.setError('can not find tso_speeds schema.');

      return true;
    }

    if (!isset(this._plant.slow_orders.tso_speeds.data) ||
        !isarray(this._plant.slow_orders.tso_speeds.data)) {

      this.setError('no tso_speeds data table.');

      return false;
    }

    const tidCol: number = (fields.get('tso_id') ?? 0)         - 1;
    const  tcCol: number = (fields.get('train_class_id') ?? 0) - 1;
    const   sCol: number = (fields.get('speed') ?? 0)          - 1;

    let bCol: number = -1;

    if (fields.has('bucket')) {

      bCol = (fields.get('bucket') ?? 0) - 1;
    }

    /* walk through the block data ... */

    let hits: number = 0;

    for (const row of this._plant.slow_orders.tso_speeds.data) {

      let    tsoId: number = row[tidCol];
      let     tcId: number = row[tcCol];
      let    speed: number = row[sCol];
      let   bucket: string = 'FRT';

      if (bCol >= 0) {

        bucket = row[bCol];
      }

      if (!this._tso.has(tsoId)) {

        this.setError(`No TSO for that TSO #id (#${tsoId}).`);
        continue ;
      }

      if (!this._trainClasses.has(tcId)) {

        this.setError(`No such train class #id (#${tcId} while hydrating TSO #${tsoId}).`);

        tcId = this.missingTrainClass;
      }

      hits++;

      let        tso: TSO|undefined|null   = this._tso.get(tsoId);
      let trainClass: TrainClass|undefined = this._trainClasses.get(tcId);

      if (!trainClass) {

        this.status(`WARNING: (TSO speed: ${tsoId}) missing/undefined train class ID: '${tcId}', no such train class ID.`);

        trainClass = this._trainClasses.get(this.missingTrainClass);
      }

      if (trainClass) {

        let ts: TSOSpeed = new TSOSpeed(
          tsoId,
          tcId,
          speed,
          trainClass,
          hits
        );

        /* track this speed as being in scope for this TSO */

        if (tso) {

          tso.addSpeedClass(bucket, tcId, ts);
        }
      }

    } /* end of data table walk */

    /* done importing connections */

    this.status('scanned ' + hits + ' tso_speeds');

    return true;
  }

  scanTSOLinks(): boolean {

    this.status('scanning TSO (tso_links)...');

    if ((typeof this._plant.slow_orders === 'undefined') ||
        (typeof this._plant.slow_orders.tso_links === 'undefined')) {

      this.setError('can not find tso_links schema.');

      return true;
    }

    const fields: Map<string, number> = this.fieldMap(this._plant.slow_orders.tso_links);

    /* make sure we can find everything */

    if (fields.size === 0) {

      this.setError('can not find tso_links schema.');

      return true;
    }

    if (!isset(this._plant.slow_orders.tso_links.data) ||
        !isarray(this._plant.slow_orders.tso_links.data)) {

      this.setError('no tso_links data table.');

      return false;
    }

    const tidCol: number = (fields.get('tso_id') ?? 0)  - 1;
    const lidCol: number = (fields.get('link_id') ?? 0) - 1;
    const   eCol: number = (fields.get('east_mp') ?? 0) - 1;
    const   wCol: number = (fields.get('west_mp') ?? 0) - 1;

    /* walk through the block data ... */

    let hits: number = 0;

    for (const row of this._plant.slow_orders.tso_links.data) {

      let    tsoId: number = row[tidCol];
      let   linkId: number = row[lidCol];
      let eastMile: number = row[eCol];
      let westMile: number = row[wCol];

      if (!this._tso.has(tsoId)) {

        this.setError(`No TSO for that TSO #id (#${tsoId}).`);
        continue ;
      }

      if (!this._links.has(linkId)) {

        this.setError(`No such link #id (#${linkId} while hydrating TSO #${tsoId}).`);
        continue ;
      }

      hits++;

      let   tso: TSO|null   = this._tso.get(tsoId) ?? null;
      let track: Track|null = this._links.get(linkId) ?? null;

      if (track) {

        let tl: TSOLink = new TSOLink(
          tsoId,
          linkId,
          eastMile,
          westMile,
          track,
          hits
        );

        /* track this link as being in scope for this TSO */

        if (tso) {

          tso.links.set(linkId, tl);
        }
      }

    } /* end of data table walk */

    /* done importing connections */

    this.status('scanned ' + hits + ' tso_links');

    return true;
  }

  scanTSO(): boolean {

    this.status('scanning TSO (slow_orders)...');

    if ((typeof this._plant.slow_orders === 'undefined') ||
        (typeof this._plant.slow_orders.slow_orders === 'undefined')) {

      this.setError('can not find slow_orders schema.');

      return true;
    }

    const fields: Map<string, number> = this.fieldMap(this._plant.slow_orders.slow_orders);

    /* make sure we can find everything */

    if (fields.size === 0) {

      this.setError('can not find slow_orders schema.');

      return true;
    }

    if (!isset(this._plant.slow_orders.slow_orders.data) ||
        !isarray(this._plant.slow_orders.slow_orders.data)) {

      this.setError('no slow_orders data table.');

      return false;
    }

    const tidCol: number = (fields.get('tso_id') ?? 0)          - 1;
    const   sCol: number = (fields.get('start') ?? 0)           - 1;
    const   dCol: number = (fields.get('duration') ?? 0)        - 1;
    const   tCol: number = (fields.get('tonnage_release') ?? 0) - 1;
    const   eCol: number = (fields.get('east_mp') ?? 0)         - 1;
    const   wCol: number = (fields.get('west_mp') ?? 0)         - 1;

    /* some columns may not be there; weren't defined in first design specification */

    let     nameCol: number = -1;
    let     descCol: number = -1;

    /* the name/desc columns may or may not be there */

    if (fields.has('name')) {

      nameCol = (fields.get('name') ?? 0) - 1;
    }

    if (fields.has('description')) {

      descCol = (fields.get('description') ?? 0) - 1;
    }

    if (fields.has('Description')) {

      descCol = (fields.get('Description') ?? 0) - 1;
    }

    /* walk through the block data ... */

    let hits: number = 0;

    for (const row of this._plant.slow_orders.slow_orders.data) {

      let tsoId       = row[tidCol];
      let nameValue   = '';
      let description = '';

      if (nameCol >= 0) {

        nameValue = row[nameCol];
      }

      if (descCol >= 0) {

        description = row[descCol];
      }

      /* initialize with the basic data ... */

      let    start: number             = this.tzSeconds(Period.toSeconds(row[sCol]));
      let duration: number             = Period.toSeconds(row[dCol]);
      let  tonnage: number             = row[tCol];
      let eastMile: number             = row[eCol];
      let westMile: number             = row[wCol];
      let   eastDP: DecisionPoint|null = null;
      let   westDP: DecisionPoint|null = null;

      /*
       * figure out the bracketing decision points, in railway terms, west is the greatest
       * mileage and east is the least mileage.
       *
       */

      const milePosts = this.milePoints();

      /* find east */

      milePosts.forEach((item: DecisionPoint) => {

        if (item._mileage > eastMile) {

          return ;
        }

        eastDP = item;
      });

      milePosts.forEach((item: DecisionPoint) => {

        if (item._mileage > westMile) {

          return ;
        }

        westDP = item;
      });

      /* time to save... */

      hits++;

      this._tso.set(tsoId, new TSO(
        tsoId,
        nameValue,
        description,
        start,
        duration,
        tonnage,
        eastMile,
        eastDP,
        westMile,
        westDP,
        hits
      ));

    } /* end of data table walk */

    /* done importing connections */

    this.status('scanned ' + hits + ' slow_order');

    return true;
  }


  scanDiversions(): boolean {

    this.status('scanning diversions...');

    if ((typeof this._plant.workblocks === 'undefined') ||
      (typeof this._plant.workblocks.diversions === 'undefined')) {

      this.setError('can not find diversions schema.');

      return true;
    }

    const fields: Map<string, number> = this.fieldMap(this._plant.workblocks.diversions);

    /* make sure we can find everything */

    if (fields.size === 0) {

      this.setError('can not find diversions schema.');

      return true;
    }

    if (!isset(this._plant.workblocks.diversions.data) ||
      !isarray(this._plant.workblocks.diversions.data)) {

      this.setError('no work blocks data table.');

      return false;
    }

    const    tidCol: number = (fields.get('train_id') ?? 0)        - 1;
    const    widCol: number = (fields.get('work_block_id') ?? 0)   - 1;
    const fromDPCol: number = (fields.get('from_dp') ?? 0)         - 1;
    const   toDPCol: number = (fields.get('to_dp') ?? 0)           - 1;
    const     dtCol: number = (fields.get('diversion_time') ?? 0)  - 1;
    const    stdCol: number = (fields.get('diversion_stdev') ?? 0) - 1;

    /* some columns may not be there; weren't defined in first design specification */

    let    divIdCol: number = -1;
    let fromLinkCol: number = -1;
    let   toLinkCol: number = -1;
    let     nameCol: number = -1;
    let     descCol: number = -1;

    /* the name/desc columns may or may not be there */

    if (fields.has('diversion_id')) {

      divIdCol = (fields.get('diversion_id') ?? 0) - 1;
    }

    if (fields.has('name')) {

      nameCol = (fields.get('name') ?? 0) - 1;
    }

    if (fields.has('description')) {

      descCol = (fields.get('description') ?? 0) - 1;
    }

    if (fields.has('from_link_id')) {

      fromLinkCol = (fields.get('from_link_id') ?? 0) - 1;
    }

    if (fields.has('to_link_id')) {

      toLinkCol = (fields.get('to_link_id') ?? 0) - 1;
    }

    /* walk through the block data ... */

    let hits: number = 0;

    for (const row of this._plant.workblocks.diversions.data) {

      let divId       = 1;
      let name        = '';
      let description = '';

      if (divIdCol >= 0) {

        divId = row[divIdCol];
      }

      if (nameCol >= 0) {

        name = row[nameCol];
      }

      if (descCol >= 0) {

        description = row[descCol];
      }

      let train: Train|null = this.trainById(row[tidCol]);

      if (!train) {

        this.status('WARNING: unknown train in diversion: ' + row[tidCol] + ' diversion #' + divId);

        continue;
      }

      if (!this._workBlocks.has(row[widCol])) {

        this.status('WARNING: unknown workblock in diversion: ' + row[widCol] + ' diversion #' + divId);

        continue;
      }

      let work: WorkBlock|null = this._workBlocks.get(row[widCol]) ?? null;

      if (!this._dps.has(row[fromDPCol])) {

        this.status('WARNING: unknown from (west) decision point in diversion: ' + row[fromDPCol] + ' diversion #' + divId);

        continue;
      }

      let fromDP: DecisionPoint|null = this._dps.get(row[fromDPCol]) ?? null;

      if (!this._dps.has(row[toDPCol])) {

        this.status('WARNING: unknown to (east) decision point in diversion: ' + row[toDPCol] + ' diverssion #' + divId);

        continue;
      }

      let toDP: DecisionPoint|null = this._dps.get(row[toDPCol]) ?? null;

      let fromTrack: Track|null = null;
      let   toTrack: Track|null = null;

      if (this._links.has(row[toLinkCol])) {

        toTrack = this._links.get(row[toLinkCol]) ?? null;
      }

      if (this._links.has(row[fromLinkCol])) {

        fromTrack = this._links.get(row[fromLinkCol]) ?? null;
      }

      let duration: number  = 0;
      let stddev: number    = 0;

      if ((row[dtCol] === undefined) || (row[dtCol] === '')) {

        this.status('WARNING: missing/empty duration in diversion #' + divId);

        continue;

      } else {

        duration = row[dtCol];
      }

      if ((row[stdCol] === undefined) || (row[stdCol] === '')) {

        this.status('WARNING: missing/empty standard deviation in diversion #' + divId);

        continue;

      } else {

        stddev = row[stdCol];
      }

      const type: DiversionType = DiversionType.OneWay;

      hits++;

      /* do we have a diversion group yet for this diversion id? */

      if (!this._diversions.has(divId)) {

        /* first time we've seen this one */

        this._diversions.set(divId, new Diversion(
          divId,
          fromDP,
          fromTrack,
          toDP,
          toTrack,
          [train],
          duration,
          stddev,
          type,
          work,
          name,
          description,
          hits
        ));

      } else {

        /* each additional one, just adds additional trains, other attributes stay the same. */

        let diversion: Diversion|null = this._diversions.get(divId) ?? null;

        /* is this train already there? */

        if (diversion && !diversion._trains.map(x => x._id).includes(train._id)) {

          diversion._trains.push(train);
        }

      }

    } /* end of data table walk */

    /* done importing connections */

    this.status('scanned ' + hits + ' diversions');

    return true;
  }

  scanWorkBlocks(): boolean {

    this.status('scanning work blocks...');

    if ((typeof this._plant.workblocks === 'undefined') ||
        (typeof this._plant.workblocks.workblocks === 'undefined')) {

      this.setError('can not find work blocks schema.');

      return true;
    }

    const fields: Map<string, number> = this.fieldMap(this._plant.workblocks.workblocks);

    /* make sure we can find everything */

    if (fields.size === 0) {

      this.setError('can not find work blocks schema.');

      return true;
    }

    if (!isset(this._plant.workblocks.workblocks.data) ||
        !isarray(this._plant.workblocks.workblocks.data)) {

      this.setError('no work blocks data table.');

      return false;
    }

    const wbidCol: number = (fields.get('work_block_id') ?? 0)  - 1;
    const fromCol: number = (fields.get('from_dp') ?? 0)        - 1;
    const   toCol: number = (fields.get('to_dp') ?? 0)          - 1;
    const    aCol: number = (fields.get('active_days') ?? 0)    - 1;
    const    sCol: number = (fields.get('daily_start') ?? 0)    - 1;
    const    dCol: number = (fields.get('daily_duration') ?? 0) - 1;
    const    tCol: number = (fields.get('logic_type') ?? 0)     - 1;

    let   nameCol: number = -1;
    let   descCol: number = -1;

    /* the name/desc columns may or may not be there */

    if (fields.has('name')) {

      nameCol = (fields.get('name') ?? 0) - 1;
    }

    if (fields.has('description')) {

      descCol = (fields.get('description') ?? 0) - 1;
    }

    /* walk through the block data ... */

    let hits: number = 0;

    for (const row of this._plant.workblocks.workblocks.data) {

      if (!this._dps.has(row[fromCol])) {

        this.status('WARNING: missing from (west) decision point in work block: ' + row[fromCol] + ' work #' + row[wbidCol]);

        continue;
      }

      if (!this._dps.has(row[toCol])) {

        this.status('WARNING: missing to (west) decision point in work block: ' + row[toCol] + ' work #' + row[wbidCol]);

        continue;
      }

      if (!isarray(row[aCol])) {

        this.status('WARNING: active work days is not an array in work #' + row[wbidCol]);

        continue;
      }

      let startSec    = 0;
      let durationSec = 0;

      if ((row[sCol] === undefined) || (row[sCol] === '')) {

        this.status('WARNING: missing/empty work start time in work #' + row[wbidCol]);

        continue;

      } else {

        startSec = this.tzSeconds(Period.toSeconds(row[sCol]));
      }

      if ((row[dCol] === undefined) || (row[dCol] === '')) {

        this.status('WARNING: missing/empty work duration time in work #' + row[wbidCol]);

        continue;

      } else {

        durationSec = Period.toSeconds(row[dCol]);
      }

      const type: WorkType = WorkBlock.typeFromString(row[tCol]);

      if (type === WorkType.NONE) {

        this.status('WARNING: could not determine type of work block in work #' + row[wbidCol]);

        continue;
      }

      hits++;

      /* save the connection in its decision point... */

      const from: DecisionPoint|null = this._dps.get(row[fromCol]) ?? null;
      const   to: DecisionPoint|null = this._dps.get(row[toCol]) ?? null;

      this._workBlocks.set(row[wbidCol], new WorkBlock(
        row[wbidCol],
        from,
        to,
        row[aCol],
        startSec,
        durationSec,
        type,
        nameCol >= 0 ? row[nameCol] : '',
        descCol >= 0 ? row[descCol] : '',
        hits
      ));

    } /* end of data table walk */

    /* done importing connections */

    this.status('scanned ' + hits + ' work blocks');

    return true;
  }

  /** @brief read in connection accel/decel data */
  scanConnections(): boolean {

    this.status('scanning connections...');

    if (typeof this._plant.connections.connection === 'undefined') {

      this.setError('can not find connections schema.');

      return false;
    }

    const fields: Map<string, number> = this.fieldMap(this._plant.connections.connection);

    /* make sure we can find everything */

    if (fields.size === 0) {

      this.setError('can not find connections schema.');

      return false;
    }

    if (!isset(this._plant.connections.connection.data) ||
        !isarray(this._plant.connections.connection.data)) {

      this.setError('no connections data table.');

      return false;
    }

    const   cidCol: number = (fields.get('connection_id') ?? 0)   - 1;
    const   didCol: number = (fields.get('dp_id') ?? 0)           - 1;
    const  fromCol: number = (fields.get('link_from') ?? 0)       - 1;
    const    toCol: number = (fields.get('link_to') ?? 0)         - 1;
    const speedCol: number = (fields.get('speed') ?? 0)           - 1;
    const  typeCol: number = (fields.get('type') ?? 0)            - 1;
    const    prCol: number = (fields.get('parallel_routes') ?? 0) - 1;
    let       hits: number = 0;

    for (const row of this._plant.connections.connection.data) {

      if (!this._links.has(row[fromCol])) {

        this.status('WARNING: missing from link: ' + row[fromCol]);

        continue;
      }

      if (!this._links.has(row[toCol])) {

        this.status('WARNING: missing to link: ' + row[toCol]);

        continue;
      }

      if (!this._dps.has(row[didCol])) {

        this.status('WARNING: missing decision point: ' + row[didCol]);

        continue;
      }

      hits++;

      /* save the connection in its decision point... */

      const  dp: DecisionPoint|null = this._dps.get(row[didCol]) ?? null;
      const src: Track|null         = this._links.get(row[fromCol]) ?? null;
      const dst: Track|null         = this._links.get(row[toCol]) ?? null;

      if (dp) {

        dp._connections.set(row[cidCol], new Connection(
          row[cidCol],
          dp,
          src,
          dst,
          row[typeCol],
          row[speedCol],
          row[prCol],
          hits
        ));


        /* the connection we add is either a junction or a terminal, then flip the flag for that */

        if (src) {

          if ((src._type === TrackType.JUNCTION) || (src._type === TrackType.TERMINAL)) {

            dp._hasTermJunc = true;
          }
        }

        if (dst) {

          if ((dst._type === TrackType.JUNCTION) || (dst._type === TrackType.TERMINAL)) {

            dp._hasTermJunc = true;

          }
        }

        /* associate links with their decision points */

        if (src && !this._dpOfLink.has(src._id)) {

          this._dpOfLink.set(src._id, new Set<DecisionPoint>());

        }

        if (src) {

          this._dpOfLink.get(src._id)?.add(dp);
        }

        if (dst && !this._dpOfLink.has(dst._id)) {

          this._dpOfLink.set(dst._id, new Set<DecisionPoint>());

        }

        if (dst) {

          this._dpOfLink.get(dst._id)?.add(dp);
        }
      }
    }

    /* done importing connections */

    this.status('scanned ' + hits + ' connections');

    return true;
  }

  /** @brief read in connection accel/decel data */
  scanConnectionPenalties(): boolean {

    this.status('scanning connection penalties...');

    if (typeof this._plant.conn_time_penalties.connection_time_penalty === 'undefined') {

      this.setError('can not find time penalties schema.');

      return false;
    }

    const fields: Map<string, number> = this.fieldMap(this._plant.conn_time_penalties.connection_time_penalty);

    /* make sure we can find everything */

    if (fields.size === 0) {

      this.setError('can not find time penalties schema.');

      return false;
    }

    if (!isset(this._plant.links.link.data) ||
        !isarray(this._plant.conn_time_penalties.connection_time_penalty.data)) {

      this.setError('no links data table.');

      return false;
    }

    const    cidCol: number = (fields.get('conn_timepenalty_id') ?? 0) - 1;
    const    tidCol: number = (fields.get('train_class_id') ?? 0)      - 1;
    const    didCol: number = (fields.get('dp_id') ?? 0)               - 1;
    const    sctCol: number = (fields.get('sw_clr_time') ?? 0)         - 1;
    const   sctsCol: number = (fields.get('sw_clr_time_stop') ?? 0)    - 1;
    const    tadCol: number = (fields.get('turnout_acc_delay') ?? 0)   - 1;
    const    tddCol: number = (fields.get('turnout_dec_delay') ?? 0)   - 1;
    const    sadCol: number = (fields.get('stop_acc_delay') ?? 0)      - 1;
    const    sddCol: number = (fields.get('stop_dec_delay') ?? 0)      - 1;
    const   hdwyCol: number = (fields.get('headway') ?? 0)             - 1;
    const hdwyspCol: number = (fields.get('headway_stop') ?? 0)        - 1;
    const hdwyslCol: number = (fields.get('headway_slow') ?? 0)        - 1;
    const  speedCol: number = (fields.get('speed') ?? 0)               - 1;
    let        hits = 0;

    for (const row of this._plant.conn_time_penalties.connection_time_penalty.data) {

      if (!this._trainClasses.has(row[tidCol])) {

        this.status('WARNING: missing train class: ' + row[tidCol]);
        continue;
      }

      if (!this._dps.has(row[didCol])) {

        this.status('WARNING: missing decision point: ' + row[didCol]);

        continue;
      }

      /* save the raw penalty */

      let trainClass: TrainClass|null = this._trainClasses.get(row[tidCol]) ?? null;
      const dp: DecisionPoint|null    = this._dps.get(row[didCol]) ?? null;

      if (!trainClass) {

        this.status(`WARNING: (penalty: ${row[cidCol]}) missing/undefined train class ID: '${row[tidCol]}', no such train class ID.`);

        trainClass = this._trainClasses.get(this.missingTrainClass) ?? null;
      }

      if (trainClass) {

        const delay: Penalty = new Penalty(
          row[cidCol],
          trainClass,
          dp,
          row[sctCol],
          row[sctsCol],
          row[tadCol],
          row[tddCol],
          row[sadCol],
          row[sddCol],
          row[hdwyCol],
          row[hdwyspCol],
          row[hdwyslCol],
          row[speedCol],
          hits
        );


        /* cache the penalties per decision point */

        if (dp) {

          dp.setPenalty(trainClass, row[speedCol], delay);
        }
      }

      hits++;
    }

    this.status('scanned ' + hits + ' accel/decel penalties.');

    return true;
  }

  /** @brief add runtimes (by train class) to the links */
  scanRuntimes(): boolean {

    this.status('scanning MRT for links ...');

    if (typeof this._plant.runtimes.runtime === 'undefined') {

      this.setError('can not find runtimes schema.');

      return false;
    }

    const fields: Map<string, number> = this.fieldMap(this._plant.runtimes.runtime);

    /* make sure we can find everything */

    if (fields.size === 0) {

      this.setError('can not find runtimes schema.');

      return false;
    }

    if (!isset(this._plant.links.link.data) ||
        !isarray(this._plant.runtimes.runtime.data)) {

      this.setError('no runtimes data table.');

      return false;
    }

    const lidCol: number = (fields.get('link_id') ?? 0)        - 1;
    const tidCol: number = (fields.get('train_class_id') ?? 0) - 1;
    const tpsCol: number = (fields.get('tps_time') ?? 0)       - 1;
    let     hits: number = 0;

    for (const row of this._plant.runtimes.runtime.data) {

      const lid: number = row[lidCol];
      const tid: number = row[tidCol];
      const tps: number = row[tpsCol];

      if (!this._links.has(lid)) {

        continue;
      }

      hits++;

      this._links.get(lid)?._mrt.set(tid, tps);
    }

    this.status('updated ' + hits + ' link runtimes.');

    return true;
  }

  /** @brief read in the decision points */
  scanDecisionPoints(): boolean {

    this.status('scanning decision points...');

    if (typeof this._plant.dps.dp === 'undefined') {

      this.setError('can not find decision points schema.');

      return false;
    }

    const fields: Map<string, number> = this.fieldMap(this._plant.dps.dp);

    /* make sure we can find everything */

    if (fields.size === 0) {

      this.setError('can not find decision points schema.');

      return false;
    }

    if (!isset(this._plant.dps.dp.data) ||
        !isarray(this._plant.dps.dp.data)) {

      this.setError('no decision points data table.');

      return false;
    }

    const  didCol: number = (fields.get('dp_id') ?? 0)   - 1;
    const nameCol: number = (fields.get('name') ?? 0)    - 1;
    const    mCol: number = (fields.get('mileage') ?? 0) - 1;

    for (const row of this._plant.dps.dp.data) {

      this._dps.set(row[didCol], new DecisionPoint(
        row[didCol],
        row[nameCol],
        row[mCol],
        this._dps.size
      ));

    }

    this.status('scanned ' + this._dps.size + ' decision points.');

    return true;
  }

  /** @brief read in the individual rail links */
  scanLinks(): boolean {

    this.status('scanning links...');

    if (typeof this._plant.links.link === 'undefined') {

      this.setError('can not find links schema.');

      return false;
    }

    const fields: Map<string, number> = this.fieldMap(this._plant.links.link);

    /* make sure we can find everything */

    if (fields.size === 0) {

      this.setError('can not find links schema.');

      return false;
    }

    if (!isset(this._plant.links.link.data) ||
        !isarray(this._plant.links.link.data)) {

      this.setError('no links data table.');

      return false;
    }

    const   lidCol: number = (fields.get('link_id') ?? 0)    - 1;
    const  physCol: number = (fields.get('phys_len') ?? 0)   - 1;
    const   useCol: number = (fields.get('use_len') ?? 0)    - 1;
    const   maxCol: number = (fields.get('max_weight') ?? 0) - 1;
    const trackCol: number = (fields.get('track') ?? 0)      - 1;
    const  typeCol: number = (fields.get('type') ?? 0)       - 1;

    for (const row of this._plant.links.link.data) {

      this._links.set(row[lidCol], new Track(
        row[lidCol],
        row[physCol],
        row[useCol],
        row[maxCol],
        row[trackCol],
        row[typeCol],
        this._links.size
      ));
    }

    this.status('scanned ' + this._links.size + ' links.');

    return true;
  }

  /** @brief read in the train classes */
  scanTrainClasses(): boolean {

    this.status('scanning train classes...');

    if (typeof this._plant.train_classes.train_class === 'undefined') {

      this.setError('can not find train class schema.');

      return false;
    }

    const fields: Map<string, number> = this.fieldMap(this._plant.train_classes.train_class);

    /* make sure we can find everything */

    if (fields.size === 0) {

      this.setError('can not find train class schema.');

      return false;
    }

    if (!isset(this._plant.train_classes.train_class.data) ||
        !isarray(this._plant.train_classes.train_class.data)) {

      this.setError('no train class data table.');

      return false;
    }

    const  tidCol: number = (fields.get('train_class_id') ?? 0)    - 1;
    const nameCol: number = (fields.get('name') ?? 0)              - 1;
    const  dirCol: number = (fields.get('direction_inc_imp') ?? 0) - 1;

    for (const row of this._plant.train_classes.train_class.data) {

      this._trainClasses.set(row[tidCol], new TrainClass(
        row[tidCol],
        row[nameCol],
        row[dirCol],
      ));
    }

    this.status('scanned ' + this._trainClasses.size + ' train classes.');

    /*
     * if we didn't scan a train class for '1' then we add a fallback that is
     * reasonably standard.
     *
     */

    if (!this._trainClasses.has(1)) {

      this.status('no train class for ID==1, adding "RTC" train class...');

      this._trainClasses.set(1, new TrainClass(
        1,
        'RTC TRAIN',
        true
      ));
    }

    /*
     * find an unallocated train class ID, and use it to allocate a fallback train
     * class to use when the train class is missing.
     *
     */

    this.missingTrainClass = 2;

    while (true) {

      if (!this._trainClasses.has(this.missingTrainClass)) {

        break;
      }

      this.missingTrainClass += 1;
    }

    this.status(`adding placeholder train class for missing train class ${this.missingTrainClass}...`)

    this._trainClasses.set(this.missingTrainClass, new TrainClass(
      this.missingTrainClass,
      'MISSING',
      true
    ));

    return true;
  }

  /** @brief read in the metadata */
  scanMeta(): boolean {

    /* get the data table columns */

    if (typeof this._plant.meta === 'undefined') {

      this.setError('can not find meta schema.');

      return false;
    }

    const fields: Map<string, number> = this.fieldMap(this._plant.meta);

    /* make sure we can find everything */

    if (fields.size === 0) {

      this.setError('can not find meta schema.');

      return false;
    }

    if (!isset(this._plant.meta.data) ||
        !isarray(this._plant.meta.data)) {

      this.setError('no  meta data table.');

      return false;
    }

    /* harvest... */

    const row        = this._plant.meta.data[0];

    this._tag      = row[(fields.get('tag') ?? 0)         - 1];
    this._id       = row[(fields.get('scenario_id') ?? 0) - 1];
    this._author   = row[(fields.get('author') ?? 0)      - 1];
    this._inputVer = row[(fields.get('version') ?? 0)     - 1];

    return true;
  }

  /** @brief fetch a dictionary of fields to their data table columns from the given schema entry */
  fieldMap(spec: any): Map<string, number> {

    const map = new Map<string, number>();

    if (!isset(spec.schema.fields) || !isarray(spec.schema.fields)) {

      /* nothing we can do */

      this.setError('no schema fields specification.');

      return map;
    }

    for (const item of spec.schema.fields) {

      map.set(item.name, item.col);
    }

    return map;
  }

  /** @brief helper to parse seconds into components */
  public secondsToDisplay(seconds: number) {

    const d: number = Math.floor(seconds / (3600 * 24));
    const h: number = Math.floor(seconds % (3600 * 24) / 3600);
    const m: number = Math.floor(seconds % 3600 / 60);
    const s: number = Math.floor(seconds % 60);

    return {
      day:    d,
      hour:   h,
      minute: m,
      second: s
    };
  }

  /** fetch the actual trains as an array */
  public actualTrains(): ActualTrain[] {

    return Array.from(this._actTrains.values());
  }

  /** fetch the even trains as an array */
  public evenTrains(): EvenTrain[] {

    return Array.from(this._evnTrains.values());
  }

  /** fetch the exponential trains as an array */
  public exponentialTrains(): ExponentialTrain[] {

    return Array.from(this._expTrains.values());
  }

  /** fetch the scheduled trains as an array */
  public scheduledTrains(): ScheduledTrain[] {

    return Array.from(this._schTrains.values());
  }

  /** fetch the weekly trains as an array */
  public weeklyTrains(): WeeklyTrain[] {

    return Array.from(this._wkyTrains.values());
  }

  /** helper to reel off the trains specified in scenario */
  public scenarioTrains(): Train[] {

    const report: Train[]  = [];
    const   sets: any    = [
      this._actTrains,
      this._evnTrains,
      this._expTrains,
      this._schTrains,
      this._wkyTrains
    ];

    /* walk through the different kinds of train templates */

    for (const trains of sets) {

      for (const train of trains.values()) {

        report.push(train);
      }
    }

    return report;
  }

  /**
   *
   * @brief helper to provide a "grid" style summary of trains, intended for use with
   * plugins like ag-grid.
   *
   *   | Train Name | Type    | Speed Class	| Length	| Tonnage	| Priority	| Origin DP + milepost	| Destination DP + milepost |
   *     Q1922	      Actual	  EB11K1_43	    11000	    8000	    33	        SYMINGTON 855.43	      DULUTH 653.61
   *
   */
  public reportTrains(): any[] {

    const report: any[]  = [];
    const   sets: any    = [
      this._actTrains,
      this._evnTrains,
      this._expTrains,
      this._schTrains,
      this._wkyTrains
    ];

    /* walk through the different kinds of train templates */

    for (const trains of sets) {

      for (const train of trains.values()) {

        const  tStr: string = Train.typeToString(train._type);
        const  cols: any[]  = [];

        /* source and destination */

        const  src: DecisionPoint = train.startDecisionPoint();
        const  dst: DecisionPoint = train.endDecisionPoint();

        /* Train Name + type + Speed class	 */

        cols.push({
                    id: train._id,
                  name: train._name,
            speedclass: train._tc._name,
               tonnage: train._tonnage,
                length: train._length,
              priority: train._priority,
                 srcmp: 'n/a',
               srcname: 'n/a',
                 dstmp: 'n/a',
               dstname: 'n/a',
             departure: 'n/a',
             dayofweek: 'n/a',
                 daily: 'n/a',
                weekly: 'n/a',
                  seed: 'n/a',
              maxdelay: 'n/a',
          percentearly: 'n/a',
                 early: 'n/a',
                  late: 'n/a',
          distribution: 'n/a'
        });

        /* depending on the type of train, fill in additional detail */

        if (src == null) {

          cols[0].srcmp   = '';
          cols[0].srcname = 'UNKNOWN';

        } else {

          cols[0].srcmp   = src._mileage;
          cols[0].srcname = src._name;
        }

        if (dst == null) {

          cols[0].dstmp   = '';
          cols[0].dstname = 'UNKNOWN';

        } else {

          cols[0].dstmp   = dst._mileage;
          cols[0].dstname = dst._name;
        }

        switch (train._type) {

          case TrainType.EVEN:
            {
              cols[0].maxdelay  = (train as EvenTrain)._overtake;
              cols[0].departure = this.secondsToDisplay((train as EvenTrain)._departure);
              cols[0].daily     = (train as EvenTrain)._perDay;
              cols[0].seed      = (train as EvenTrain)._seed;
              cols[0].early     = (train as EvenTrain)._stdDev;
            }
            break;

          case TrainType.EXPONENTIAL:
            {
              cols[0].maxdelay  = (train as ExponentialTrain)._overtake;
              cols[0].daily     = (train as ExponentialTrain)._perDay;
              cols[0].seed      = (train as ExponentialTrain)._seed;
            }
            break;

          case TrainType.ACTUAL:
            {
              cols[0].maxdelay  = (train as ActualTrain)._overtake;
              cols[0].departure = this.secondsToDisplay((train as ActualTrain)._departure);
            }
            break;

          case TrainType.SCHEDULED:
            {
              cols[0].maxdelay     = (train as ScheduledTrain)._overtake;
              cols[0].departure    = this.secondsToDisplay((train as ScheduledTrain)._departure);
              cols[0].dayofweek    = (train as ScheduledTrain)._days;
              cols[0].seed         = (train as ScheduledTrain)._seed;
              cols[0].percentearly = (train as ScheduledTrain)._percentEarly;
              cols[0].early        = (train as ScheduledTrain)._stdDevEarly;
              cols[0].late         = (train as ScheduledTrain)._stdDevLate;
            }
            break;

          case TrainType.WEEKLY:
            {
              cols[0].maxdelay     = (train as WeeklyTrain)._overtake;
              cols[0].weekly       = this.secondsToDisplay((train as WeeklyTrain)._perWeek);
              cols[0].seed         = (train as WeeklyTrain)._seed;
              cols[0].distribution = (train as WeeklyTrain)._distId;
            }
            break;

        }

        /* type */

        cols.push(tStr);

        /* speed class */

        cols.push({id: train._tc._id, name: train._tc._name, dir: TrainClass.typeToString(train._tc._dir)});

        /* length */

        cols.push(train._length);

        /* tonnage */

        cols.push(train._tonnage);

        /* priority */

        cols.push(train._priority);

        if (src == null) {

          cols.push({id: -1, name: 'UNKNOWN', mileage: ''});

        } else {

          cols.push({id: src._id, name: src._name, mileage: src._mileage});
        }

        if (dst == null) {

          cols.push({id: -1, name: 'UNKNOWN', mileage: ''});

        } else {

          cols.push({id: dst._id, name: dst._name, mileage: dst._mileage});
        }

        /* accumulate rows in the grid */

        report.push(cols);
      }

    }

    /* all done */

    return report;
  }

  /**
   * @brief helper to provide a "grid" style summary of plant structure, intended for use
   * with plugins like ag-grid.
   *
   */
  public reportPlant(): any[] {

    const report: any[]  = [];
    let maxTrack: number = 0;

    /*
     * pre-scan interlinks to find max number of tracks.  for each row in our report, we
     * have to report each track as a subgroup of columns.  All rows need to have the same
     * number of track subgroups (even though some may be empty).  So up front, we have to
     * know the max number of tracks we can see in this network.
     *
     */

    for (const bundle of this._interlinks) {

      if (bundle._tracks.length > maxTrack) {

        maxTrack = bundle._tracks.length;
      }
    }

    /* template for a default track */

    const defaultTrack = {
      type:      '.',
      number:    '.',
      layout:    '.',
      junction:  '.',
      length:    '.',
      useable:   '.',
      maxWeight: '.'
    };

    /* now walk the interlinks in order from west to east (max mileage to 0) */

    for (const bundle of this._interlinks) {

      /* build the report row for this interlink */

      const row: any[]        = [];

      if (report.length > 0 && bundle._before) {

        if (bundle._before._mileage === report[report.length - 1][0]) {

          /* we just pushed this row */

          continue;
        }
      }

      /* col 0 mileage */

      row.push(bundle._before ? bundle._before._mileage : -1);

      /* col 1 name */

      row.push(bundle._before ? bundle._before._name : '<unknown>');

      /* col 2 type */

      row.push(InterLink.typeToString(bundle._type));

      /* remaining columns are subgroups for each track, fill in default tracks... */

      for (let i = 0; i < maxTrack; i++) {

        /* use spread to get a clone of col group template. basically 'new' of the anonymous object */

        row.push({...defaultTrack});
      }

      /* now overwrite with the tracks we have for this bundle... */

      for (let j = 0; j < bundle._tracks.length; j++) {

        const t: Track = bundle._tracks[j];

        row[3 + j] = {
          type:      Track.typeToString(t._type),
          number:    t._track,
          layout:    bundle.switchLayout(t),
          junction:  bundle.junctionLayout(),
          length:    Math.round(t._physLength * 5280.0),
          useable:   Math.round(t._useLength * 5280.0),
          maxWeight: t._maxWeight
        };
      }

      /* save the row */

      report.push(row);
    }

    return report;
  }

  /** fetch the sorted (max mileage at [0]) interlinks */
  public interlinks(): InterLink[] {

    return this._interlinks;
  }

  /** @brief helper to map a decision point to the interlink where it's the west side ( > mileage ) decision point */
  public interLinkOfDecisionPoint(dp: DecisionPoint): InterLink|null {

    for (const bundle of this._interlinks) {

      // noinspection JSUnusedLocalSymbols
      const [dp1, dummy] = bundle.dps();

      if (dp1) {

        if (dp1._id === dp._id) {

          return bundle;
        }
      }
    }

    /* can't find it */

    return null;
  }

  /** @brief helper to get a generic summary of train templates */
  public reportTrainTemplates(): any {

    const report: any = {
      actual:      [],
      even:        [],
      exponential: [],
      scheduled:   [],
      weekly:      []

    };

    {
      for (const train of this._actTrains.values()) {

        report.actual.push({
                  id: train._id,
                name: train._name,
               paths: train._paths,
          trainClass: train._tc
        });

      }

      report.actual.sort((a: any, b: any): number => {

        if (a.name < b.name) {

          return -1;
        }

        if (a.name > b.name) {

          return 1;
        }

        return 0;
      });
    }

    {
      for (const train of this._evnTrains.values()) {

        report.even.push({
                  id: train._id,
                name: train._name,
               paths: train._paths,
          trainClass: train._tc
        });

      }

      report.even.sort((a: any, b: any): number => {

        if (a.name < b.name) {

          return -1;
        }

        if (a.name > b.name) {

          return 1;
        }

        return 0;
      });
    }

    {
      for (const train of this._expTrains.values()) {

        report.exponential.push({
                  id: train._id,
                name: train._name,
               paths: train._paths,
          trainClass: train._tc
        });

      }

      report.exponential.sort((a: any, b: any): number => {

        if (a.name < b.name) {

          return -1;
        }

        if (a.name > b.name) {

          return 1;
        }

        return 0;
      });
    }

    {
      for (const train of this._schTrains.values()) {

        report.scheduled.push({
                  id: train._id,
                name: train._name,
               paths: train._paths,
          trainClass: train._tc
        });

      }

      report.scheduled.sort((a: any, b: any): number => {

        if (a.name < b.name) {
          return -1;
        }

        if (a.name > b.name) {
          return 1;
        }

        return 0;
      });
    }

    {
      for (const train of this._wkyTrains.values()) {

        report.weekly.push({
                  id: train._id,
                name: train._name,
               paths: train._paths,
          trainClass: train._tc
        });

      }

      report.weekly.sort((a: any, b: any): number => {

        if (a.name < b.name) {

          return -1;
        }

        if (a.name > b.name) {

          return 1;
        }

        return 0;
      });
    }

    /* pass it back */

    return report;
  }

  public formatHours(time: number) {

    const            hours: number = Math.floor(time);
    const minutesRemainder: number = (time - hours) * 60;
    let            minutes: number = Math.floor(minutesRemainder);

    if (minutes > 59) {
      minutes = 59;
    }

    const secondsRemainder: number = (minutesRemainder - minutes) * 60;
    let            seconds: number = Math.round(secondsRemainder);

    if (seconds > 59) {
      seconds = 59;
    }

    return hours.toString().padStart(2, '0') +
      ':' +
      minutes.toString().padStart(2, '0') +
      ':' +
      seconds.toString().padStart(2, '0');
  }

  /** @brief bulk train stop reporter */
  public reportStops(trains: Train[]): TrainStopReport[] {

    const report: TrainStopReport[] = [];

    for (const train of trains) {

      const stopData: TrainStopEntry[] = this.reportTrainStops(train);

      const   item: TrainStopReport  = {
             stops: stopData,
             train,
         totalTime: '00:00:00',
        totalHours: 0.0
      };

      for (const stop of stopData) {

        item.totalHours += stop.hours;
      }

      item.totalTime = this.formatHours(item.totalHours);

      /* save data for this train */

      report.push(item);
    }

    /* pass back the bulk report */

    return report;
  }

  /** @brief provide table (grid-like) report of stop info, specific to this train, for all decision points */
  public reportTrainStops(train: Train): TrainStopEntry[] {

    const report: TrainStopEntry[] = [];

    /* now walk the interlinks in order from west to east (max mileage to 0) */

    for (let index = 0; index < this._interlinks.length; index++) {

      /* build the report row for this interlink */

      const bundle: InterLink|null     = this._interlinks[index] ?? null;
      const     dp: DecisionPoint|null = bundle._before ?? null;

      if (!dp) {

        /* missing dp? */

        continue ;
      }

      if (report.length > 0) {

        if (dp._mileage === report[report.length - 1].mileage) {

          /* we just pushed this row */

          continue;
        }
      }

      const rd: TrainStopEntry = {

           dpId: dp._id,
           name: dp._name,
        mileage: dp._mileage,
           type: InterLink.typeToString(bundle._type),
         bundle: index,
           time: '',
          hours: 0.0,
           stop: null

      };

      /*
       * to find the applicable stop, we have to look for one of the valid links
       * touching this decision point and then the other end of that link, going in
       * the train's direction of travel.  for eastbound this dp must be less
       * mileage than the other end, for westbound it must be greater than the
       * other end.
       *
       */

      for (const [cid, con] of dp._connections) {

        /* pick the track that makes the train arrive at this decision point */

        let incoming: Track|null         = null;
        let  otherDP: DecisionPoint|null = null;

        if (!con) {

          /* missing connection? */

          continue ;
        }

        /* try the to link... */

        if ((con._to && con._to._after == null) || (con._to && con._to._before == null)) {

          if ((con._to._after != null) && (con._to._after._id === dp._id)) {

            incoming = con._to;

          } else if ((con._to._before != null) && (con._to._before._id === dp._id)) {

            incoming = con._to;
          }

        } else {

          if (con._to) {

            if (con._to._after && con._to._after._id === dp._id) {

              otherDP = con._to._before;

            } else {

              otherDP = con._to._after;
            }
          }

          if (train._tc && train._tc._dir === TrainDirection.EAST) {

            if (otherDP && otherDP._mileage > dp._mileage) {

              incoming = con._to;
            }

          } else {

            if (otherDP && otherDP._mileage < dp._mileage) {

              incoming = con._to;
            }
          }
        }

        /* did the to link work? */

        if (!incoming) {

          /* try the from link... */

          if ((con._from && con._from._after == null) || (con._from && con._from._before == null)) {

            if ((con._from._after != null) && (con._from._after._id === dp._id)) {

              incoming = con._from;

            } else if ((con._from._before != null) && (con._from._before._id === dp._id)) {

              incoming = con._from;
            }

          } else {

            if (con._from) {

              if (con._from._after && con._from._after._id === dp._id) {

                otherDP = con._from._before;

              } else {

                otherDP = con._from._after;
              }
            }

            if (train._tc && train._tc._dir === TrainDirection.EAST) {

              if (otherDP && otherDP._mileage > dp._mileage) {

                incoming = con._from;
              }

            } else {

              if (otherDP && otherDP._mileage < dp._mileage) {

                incoming = con._from;
              }
            }
          }
        }

        if (!incoming) {

          /* can't identify plausible incoming track */

          if (train._tc) {

            this.setError(
              'can not identify incoming track for connection: ' +
              cid +
              ' in direction: ' +
              TrainClass.typeToString(train._tc._dir)
            );
          }

          continue;
        }

        /* look for a stop that has this link as one of its valid links */

        for (const stop of train._stops) {

          /*
           * if train exits network at this bundle, then it has to use the junction, otherwise
           * it has to use a regular link.
           *
           */

          if (stop._clears_main) {

            /* try find at least one junction; they should all connect to the same DP */

            if (incoming._type !== TrackType.JUNCTION) {

              continue ;
            }

          } else {

            if (incoming._type === TrackType.JUNCTION) {

              continue ;
            }

          }

          if (!stop._tracks.has(incoming._id)) {

            /* this train stop doesn't apply for this decision point and track */

            continue ;
          }

          if (rd.stop != null) {

            this.setError(
              'Found more than one stop for train at decision point! Ignoring stop: ' +
              stop._id +
              ' ' +
              stop._name
            );

            continue;
          }

          rd.stop = stop;

        } /* end of stop walk */

      } /* end of connection walk at decision point */

      if (rd.stop == null) {

        /* we didn't find any applicable stop for this train at this decision point. */

        /* save the info this decision point */

        report.push(rd);

        continue;
      }

      /* once we have the stop, we can calculate the expected time of the activities */

      let total: number = 0.0;

      for (const event of rd.stop._events) {

        total += event._chance * event._mean_hour;
      }

      rd.time  = this.formatHours(total);
      rd.hours = total;

      if ((report.length > 0) && (report[report.length - 1].stop != null)) {

        let s: Stop|null = report[report.length - 1].stop ?? null;

        if (s && (s._id === rd.stop._id)) {

          /* stops can only show up once */

          this.setError('Trying to add same stop, multiple times: ' + rd.stop._id + ' ' + rd.stop._name);

          continue ;
        }
      }

      /* save the info this decision point */

      report.push(rd);

    } /* end of interlink walk */

    /* pass it back */

    return report;
  }
}
